import type { PickerLocale } from "antd/lib/date-picker/generatePicker";
import DatePickerLocale from "antd/lib/date-picker/locale/it_IT";
import TimePickerLocale from "antd/lib/time-picker/locale/it_IT";
import * as i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

const i18n = i18next
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    debug: false,
    fallbackLng: "it",
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    resources: {
      it: {
        translation: {
          common: {
            yes: "Sì",
            no: "No"
          },
          map: {
            basemap: {
              title: "Mappa",
              clean: "Standard",
              detailed: "Dettagliata",
              satellite: "Satellite",
            },
          },
          models: {
            secRole: {
              relationTitle: "Visualizza Ruoli",
              tableTitle: "Gestione Ruoli",
              singleElement: "Ruolo",
              authority: "Nome",
              privilegeIds: "Privilegi",
              managePrivileges: "Privilegi",
              managePrivilegesLabel: "Modifica privilegi",
            },
            secPrivilege: {
              relationTitle: "Visualizza Privilegi",
              tableTitle: "Gestione Privilegi",
              singleElement: "Privilegio",
              name: "Nome",
            },
            secUser: {
              relationTitle: "Visualizza Utenti",
              tableTitle: "Gestione Utenti",
              singleElement: "Utente",
              username: "Username",
              password: "Password",
              confirmPassword: "Conferma password",
              passwordMismatch: "Le password inserite non coincidono.",
              changePassword: "Password",
              changePasswordLabel: "Modifica password",
              enabled: "Abilitato",
              firstName: "Nome",
              lastName: "Cognome",
              email: "Email",
              dateCreated: "Data creazione",
              lastUpdate: "Ultima modifica",
              lastConnection: "Ultimo login",
              roleIds: "Ruoli",
              manageRoles: "Ruoli",
              manageRolesLabel: "Modifica ruoli",
            },
            user: {
              relationTitle: "Visualizza Utenti",
              tableTitle: "Gestione Utenti",
              singleElement: "Utente",
              username: "Username",
              enabled: "Abilitato",
              firstName: "Nome",
              lastName: "Cognome",
              email: "Email",
              lastLoginAt: "Ultimo login",
              roleCodes: "Ruoli",
              teams: "Team",
              teamId: "Team",
              teamIds: "Team",
              teamsDetachTitle: "Rimuovere l'utente dai team?",
              teamsDetachContent:
                "Rimuovendo il ruolo Operatore, al salvataggio l'utente verrà scollegato dai team: {{teams}}.",
              teamsDetachOk: "Rimuovi e salva",
              teamsDetachCancel: "Annulla",
              saveStep: {
                profile: "Aggiornamento profilo",
                teams: "Aggiornamento team",
                roles: "Aggiornamento ruoli",
              },
              temporaryPassword: "Password provvisoria",
              confirmPassword: "Conferma password",
              createdAt: "Data creazione",
              updatedAt: "Ultima modifica",
              showDeleted: "Mostra eliminati",
              resetPassword: {
                tooltip: "Reimposta password",
                title: "Reimposta password — {{username}}",
                newPassword: "Nuova password",
                confirmPassword: "Conferma password",
                temporary: "Password provvisoria",
                submit: "Reimposta password",
                success: "Password reimpostata correttamente.",
                error:
                  "Attenzione! Si è verificato un errore durante il reset della password.",
              },
            },
            role: {
              relationTitle: "Visualizza Ruoli",
              tableTitle: "Gestione Ruoli",
              singleElement: "Ruolo",
              code: "Codice",
              displayName: "Nome",
              description: "Descrizione",
              privilegeCodes: "Privilegi",
              readOnlyHint: "Modifica di codice, nome e descrizione non ancora supportata dal backend. È possibile modificare solo i privilegi.",
            },
            team: {
              relationTitle: "Visualizza Team",
              tableTitle: "Gestione Team",
              singleElement: "Team",
              name: "Nome",
              description: "Descrizione",
              memberCount: "Membri",
              createdAt: "Data creazione",
              updatedAt: "Ultima modifica",
              showDeleted: "Mostra eliminati",
              members: "Membri del team",
              membersDrawer: {
                title: "Membri del team: {{name}}",
                titleFallback: "Membri del team",
                empty: "Nessun membro in questo team.",
              },
            },
            meterModel: {
              relationTitle: "Visualizza Modelli Contatore",
              tableTitle: "Modelli Contatore",
              singleElement: "Modello Contatore",
              vendor: "Produttore",
              modelCode: "Nome",
              encoderType: "Tipo encoder",
              omsVersion: "Versione OMS",
              notes: "Note",
              status: {
                title: "Stato",
                ACTIVE: "Attivo",
                DEACTIVATED: "Disattivato",
              },
              createdAt: "Data creazione",
              deactivate: "Disattiva",
              deactivateConfirm: {
                title: "Disattivare il modello selezionato?",
                description: "Il modello sparirà dalla lista assegnabile dei tenant; i contatori storici che lo referenziano non vengono toccati.",
                confirm: "Disattiva",
                cancel: "Annulla",
              },
              deactivateSuccess: "Modello disattivato correttamente.",
            },
            firmware: {
              tableTitle: "Firmware",
              singleElement: "Firmware",
              deviceModel: "Modello",
              version: "Versione",
              fileName: "File",
              sizeBytes: "Dimensione",
              checksum: "Checksum (sha256)",
              releaseNotes: "Note di rilascio",
              uploadedBy: "Caricato da",
              createdAt: "Data caricamento",
              inUse: "In uso",
              status: {
                title: "Stato",
                DRAFT: "Bozza",
                PUBLISHED: "Pubblicato",
                ARCHIVED: "Archiviato",
              },
              upload: "Carica firmware",
              versionInvalid: "Formato versione non valido (es. 1.2.0).",
              fileRequired: "Selezionare un file firmware.",
              uploadSuccess: "Firmware caricato (stato Bozza).",
              publish: "Pubblica",
              publishConfirm: {
                title: "Pubblicare questa release?",
                description: "Diventerà la versione scaricabile dagli operatori (la più alta PUBLISHED per il modello).",
                confirm: "Pubblica",
                cancel: "Annulla",
              },
              publishSuccess: "Release pubblicata.",
              archive: "Archivia",
              archiveConfirm: {
                title: "Archiviare questa release?",
                description: "Non sarà più scaricabile dagli operatori.",
                confirm: "Archivia",
                cancel: "Annulla",
              },
              archiveSuccess: "Release archiviata.",
            },
            device: {
              tableTitle: "Dispositivi",
              fleetTitle: "Fleet dispositivi",
              singleElement: "Dispositivo",
              serialNumber: "Seriale",
              deviceType: "Tipo",
              hwVersion: "Versione HW",
              osVersion: "Versione OS",
              bleMac: "MAC BLE",
              wifiMac: "MAC WiFi",
              fwVersion: "Firmware",
              ccid: "CCID",
              battery: "Batteria",
              lastOperator: "Ultimo operatore",
              lastSeenAt: "Ultimo contatto",
              tenant: "Tenant",
              tenantFilter: "Filtra per tenant",
              updateAvailable: "Da aggiornare",
              status: { title: "Stato", ACTIVE: "Attivo", DECOMMISSIONED: "Dismesso" },
              health: {
                title: "Salute",
                none: "Nessuna telemetria ricevuta",
                batteryPct: "Batteria",
                bleMeanRssi: "RSSI BLE medio",
                wifiState: "Stato WiFi",
                totalReceived: "Frame ricevuti",
                crcError: "Errori CRC",
                otherErrors: "Altri errori",
                modeC: "Mode C",
                modeT: "Mode T",
                receivedAt: "Rilevato il",
              },
              alarm: {
                title: "Allarmi recenti",
                alarmType: "Tipo",
                observedAt: "Osservato",
                operatorRef: "Operatore",
                empty: "Nessun allarme recente",
              },
              alarmType: {
                LOW_BATTERY: "Batteria bassa",
                INTERNAL: "Interno",
                CONFIG: "Configurazione",
                HIGH_TEMP_MCU: "Temperatura MCU alta",
                HIGH_MCU_BATT: "Temperatura batteria alta",
                WMBUS_RADIO: "Radio wM-Bus",
                TIMESYNC: "Sincronizzazione ora",
                BATTERY_READ: "Lettura batteria",
              },
              decommission: "Dismetti",
              decommissionConfirm: {
                title: "Dismettere questo dispositivo?",
                description: "Lo stato passa a Dismesso. L'operazione è idempotente; la telemetria successiva non lo riattiva.",
                confirm: "Dismetti",
                cancel: "Annulla",
              },
              decommissionSuccess: "Dispositivo dismesso.",
            },
            tenant: {
              singleElement: "Tenant",
              tableTitle: "Tenant",
              name: "Nome",
              slug: "Slug",
              slugPattern: "Solo minuscole, cifre e trattini",
              keycloakRealmName: "Realm",
              createdAt: "Creato il",
              provisionedAt: "Attivato il",
              updatedAt: "Aggiornato il",
              initialAdminEmail: "Email admin iniziale",
              initialAdminFirstName: "Nome admin",
              initialAdminLastName: "Cognome admin",
              notFound: "Tenant non trovato",
              notFoundHint: "Il tenant richiesto non esiste o è stato rimosso.",
              backToList: "Torna all'elenco",
              status: {
                title: "Stato",
                PROVISIONING: "In provisioning",
                ACTIVE: "Attivo",
                SUSPENDED: "Sospeso",
                DEGRADED: "Degradato",
                ARCHIVED: "Archiviato",
              },
              suspend: "Sospendi",
              resume: "Riattiva",
              sync: "Sincronizza",
              syncAll: "Sincronizza tutti",
              syncAllSuccess: "Sincronizzati {{count}} tenant",
              syncAllDegradedTitle: "Alcuni tenant sono degradati",
            },
            subTenant: {
              singleElement: "Ambiente",
              tableTitle: "Ambienti",
              relationTitle: "Ambienti",
              subSlug: "Slug ambiente",
              schemaName: "Schema",
              createdAt: "Creato il",
              initialAdminEmail: "Email admin iniziale",
              initialAdminFirstName: "Nome admin",
              initialAdminLastName: "Cognome admin",
              create: "Crea ambiente",
              status: { title: "Stato", ACTIVE: "Attivo", RETIRED: "Ritirato" },
            },
            persona: {
              singleElement: "Persona",
              tableTitle: "Persone",
              relationTitle: "Persone",
              username: "Username",
              firstName: "Nome",
              lastName: "Cognome",
              email: "Email",
              enabled: "Abilitato",
              memberships: "Appartenenze",
              edit: "Modifica identità",
              detail: "Dettaglio",
              erase: "Elimina",
              eraseConfirm: {
                title: "Eliminare la persona?",
                description: "Rimuove l'utente da tutti gli ambienti del tenant (dati storici preservati). Irreversibile.",
                confirm: "Elimina",
                cancel: "Annulla",
              },
              enableSuccess: "Stato aggiornato",
              editSuccess: "Identità aggiornata",
              eraseSuccess: "Persona eliminata",
              consistencyNote: "Le modifiche a identità/appartenenze hanno effetto al prossimo login/refresh del token.",
            },
            geoCoverage: {
              title: "Copertura mappe",
              intro: "La mappa è sempre tutta l'Italia; qui si scelgono le aree con il routing stradale on-premise, regioni intere o singole province. Ogni area ha il suo grafo: si costruisce da sola quando la si abilita, esce subito dal servizio quando la si toglie, e grafi e mappa si rinnovano insieme una notte alla settimana.",
              rule: "Una regione e una sua provincia non si scelgono insieme: con la regione intera le sue province sono disattivate, e con una provincia la sua regione.",
              loadError: "Caricamento della copertura non riuscito.",
              reload: "Ricarica",
              pollError: "Aggiornamento automatico interrotto dopo ripetuti errori: i dati mostrati possono non essere attuali.",
              resume: "Riprendi",
              status: {
                title: "Mappa e routing",
                noReport: "Il backend non ha ancora letto lo stato del builder: fase e stime non sono disponibili.",
                phase: "Fase",
                progress: "{{current}} di {{total}}",
                revision: "Revisione",
                revisionNone: "nessuna",
                // Code dopo il numero di revisione, reso a parte in grassetto.
                revisionApplied: ", applicata",
                revisionApplying: ", in applicazione: le aree entrano in servizio una alla volta",
                routing: "Routing in esercizio",
                routingNone: "Niente in esercizio",
                routingEmpty: "nessuna area",
                routingCount_one: "{{count}} area",
                routingCount_other: "{{count}} aree",
                routingNamesMore_one: "{{names}} e un'altra",
                routingNamesMore_other: "{{names}} e altre {{count}}",
                basemap: "Mappa",
                basemapReady: "Italia intera",
                basemapBuilt: "costruita il {{date}}",
                basemapMissing: "non ancora costruita",
                vintage: "Dati OpenStreetMap",
                vintageOne: "del {{date}}",
                vintageDiverged: "grafi del {{graph}}, mappa del {{basemap}}: ",
                vintageDivergedTail: "annate diverse finché il prossimo aggiornamento settimanale non le riallinea",
                refresh: "Aggiornamento settimanale",
                refreshInProgress: "in corso",
                refreshNever: "non ancora eseguito",
                refreshOutcome: "{{outcome}} il {{date}}",
                refreshNext: "prossimo: {{date}}",
                lastReportLabel: "Ultima lettura del builder:",
                readAt: "letto alle {{time}}",
                stale: "Il backend non legge il builder da più di {{minutes}} minuti: fase e stati delle aree sono l'ultima notizia, non il presente.",
                retry: "Riprova",
                failedAreas_one: "{{count}} area non è entrata in servizio",
                failedAreas_other: "{{count}} aree non sono entrate in servizio",
                failedAreasRetry: "Il motivo è sulla riga di ciascuna. «Riprova» le ritenta con una revisione nuova dello stesso contenuto.",
                failedAreasVintage: "Il motivo è sulla riga di ciascuna: le aree respinte per annata aspettano il prossimo aggiornamento settimanale, e «Riprova» non le sblocca prima.",
                failedAreasMixed: "Il motivo è sulla riga di ciascuna. «Riprova» ritenta quelle fallite per altri motivi; quelle respinte per annata aspettano il prossimo aggiornamento settimanale, e «Riprova» non le sblocca prima.",
              },
              phase: {
                IDLE: "A riposo",
                DOWNLOADING: "Scarico dei dati OpenStreetMap",
                CLIPPING: "Ritaglio delle aree",
                BUILDING_TILES: "Costruzione della mappa",
                BUILDING_GRAPH: "Costruzione del grafo stradale",
                SWAPPING: "Messa in esercizio",
              },
              state: { DISABLED: "Non attiva", PENDING: "In attesa", BUILDING: "In costruzione", READY: "Attiva", FAILED: "Fallita", partial: "Parziale · {{served}} di {{total}}" },
              build: {
                kind: { GRAPH: "Costruzione del grafo", BASEMAP: "Costruzione della mappa", REFRESH: "Aggiornamento settimanale", ROLLBACK: "Ripristino" },
                gender: { GRAPH: "female", BASEMAP: "female", REFRESH: "male", ROLLBACK: "male" },
                outcome: {
                  DONE: "riuscita", FAILED: "fallita", REJECTED: "rifiutata", INTERRUPTED: "interrotta", UNCHANGED: "senza cambiamenti",
                  DONE_male: "riuscito", FAILED_male: "fallito", REJECTED_male: "rifiutato", INTERRUPTED_male: "interrotto",
                },
              },
              refreshOutcome: { UPDATED: "aggiornato", UNCHANGED: "nessun cambiamento", FAILED: "fallito" },
              toRemove: "da rimuovere",
              changed: "modificata",
              unreachable: "non risponde",
              progress: {
                title: "Attivazione in corso",
                areas_one: "{{settled}} di {{count}} area",
                areas_other: "{{settled}} di {{count}} aree",
                failed_one: "{{count}} fallita",
                failed_other: "{{count}} fallite",
                current: "{{area}}: {{step}}, passo {{index}} di {{steps}}",
                currentNoStep: "{{area}}: passo {{index}} di {{steps}}",
                remaining: "circa {{minutes}} min",
                remainingSoon: "meno di un minuto",
                updated: "aggiornato {{ago}}",
                steps: {
                  source: "scarico dell'estratto",
                  clip: "ritaglio",
                  "osrm-extract": "estrazione del grafo",
                  "osrm-contract": "contrazione del grafo",
                  swap: "messa in esercizio",
                  verify: "verifica",
                },
              },
              columns: {
                area: "Area",
                state: "Stato",
                graphRam: "RAM grafo",
                graphRamHint: "RAM stimata del grafo a regime, finché l'area è servita: è questa che il tetto del builder limita.",
                extractPeakRam: "Picco estrazione",
                extractPeakRamHint: "RAM stimata al picco di osrm-extract, mentre il grafo dell'area si costruisce, non a regime. Il builder costruisce un'area alla volta e la rifiuta (EXTRACT_RAM_INSUFFICIENT) se il picco supera la RAM libera in quel momento: la console non lo sa in anticipo e non blocca «Applica».",
                buildTime: "Tempo build",
                buildTimeValue: "{{min}} min",
                pendingRuns: "Giri in attesa",
              },
              pendingRuns: {
                title: "Giri in attesa",
                count_one: "{{count}} giro",
                count_other: "{{count}} giri",
                intro: "Giri che aspettano il routing di quest'area: entrano in programmazione quando il grafo è in servizio.",
                open: "Giri in attesa di {{area}}: {{count}}",
                empty: "Nessun giro in attesa di quest'area.",
                columns: {
                  tenant: "Cliente",
                  subTenant: "Nucleo",
                  run: "Giro",
                  request: "Campagna",
                  expiresAt: "Scadenza",
                  candidates: "Aree candidate",
                  detectedAt: "Rilevato il",
                },
              },
              areas: {
                title: "Routing stradale on-premise (OSRM)",
                subtitle: "Aree con il grafo delle rotte: regioni intere o singole province",
                activeCount: "Aree attive · {{count}}",
                goTo: "Vai a {{area}}",
                notShown: "Non è fra le righe mostrate",
                infoLabel: "Note sulla RAM e sulla scelta delle aree",
                filterLabel: "Aree da mostrare",
                filterActive: "Attive",
                filterAll: "Tutte",
                search: "Cerca regione o provincia",
                empty: "Nessuna area corrisponde",
                expand: "Apri {{area}}",
                collapse: "Chiudi {{area}}",
                provinceCount_one: "{{count}} provincia",
                provinceCount_other: "{{count}} province",
              },
              bar: {
                noEstimates: "stime non disponibili",
                ramLabel: "RAM dei grafi",
                ofCap: "su {{cap}}",
                noCap: "tetto non dichiarato",
                upperBound: "La somma è un limite superiore: la memoria di un grafo servito sta sotto la stima.",
                serverEstimate: "Tempo build e picco sono stimati sul server attuale",
                ramBar: "RAM dei grafi: {{ram}} su {{cap}}",
                overCap: "La selezione supera il tetto di RAM dei grafi del builder ({{ram}} su {{cap}}): togli qualche area.",
                tooMany: "Il builder serve al più {{max}} aree insieme: ne hai scelte {{count}}.",
                apply: "Applica",
              },
              summary: {
                free: "{{free}} liberi",
                over: "{{over}} oltre il tetto",
                pendingNone: "nessuno",
                pendingAll: "tutti in",
                pendingIn: "in",
                pendingAnd: "e",
                pendingMore: "e altre {{count}} aree",
              },
              confirm: {
                title: "Applicare la nuova copertura?",
                added: "Da aggiungere",
                removed: "Da togliere",
                none: "nessuna",
                removedNow: "Le aree tolte escono subito dal servizio.",
                nothingToBuild: "Nessuna area da costruire.",
                minutes_one: "Da costruire: {{count}} area, circa {{minutes}} minuti.",
                minutes_other: "Da costruire: {{count}} aree, una alla volta: al più {{minutes}} minuti in tutto, ed entrano in servizio una dopo l'altra.",
                minutesUnknown: "Tempo stimato di costruzione non disponibile.",
                mapUnchanged: "La mappa non cambia: la selezione governa solo il routing.",
                empty: "Nessuna area selezionata: il routing ricadrà sulla distanza in linea d'aria. La mappa dell'Italia resta.",
                building: "Una costruzione è in corso: la nuova revisione partirà al suo termine.",
                ok: "Applica",
                cancel: "Annulla",
              },
              applied: "Revisione {{revision}} creata.",
              stale409: "Un altro amministratore ha applicato la revisione {{latestRevision}} nel frattempo: la pagina è aggiornata e la tua selezione è rimasta. Rivedi le righe modificate e applica di nuovo.",
              unknown422: "Il backend non conosce questi codici di area: {{codes}}.",
              overlap422: "Una regione e una sua provincia non si possono scegliere insieme: {{pairs}}.",
            },
            globalPersona: {
              singleElement: "Persona",
              tableTitle: "Persone (cross-realm)",
              username: "Username",
              tenantCount: "N. tenant",
              detailTitle: "Dettaglio persona",
              tenantsTitle: "Tenant",
              enabled: "Abilitato",
              disabled: "Disabilitato",
            },
            meter: {
              relationTitle: "Visualizza Contatori",
              tableTitle: "Gestione Contatori",
              singleElement: "Contatore",
              wmbusAddress: "Indirizzo wM-Bus",
              modelId: "Modello",
              serialNumber: "Numero di serie",
              latitude: "Latitudine",
              longitude: "Longitudine",
              address: "Indirizzo",
              notes: "Note",
              status: {
                title: "Stato",
                ACTIVE: "Attivo",
                DEACTIVATED: "Disattivato",
              },
              registeredAt: "Data registrazione",
              // Chiavi lette anche da MeterPage.fieldFilterLabel, che compone
              // `models.meter.<key>` per l'etichetta dei chip in vista Mappa.
              zoneId: "Zona",
              zoneAssignedAt: "Data assegnazione zona",
              deactivate: "Disattiva",
              deactivateConfirm: {
                title: "Disattivare il contatore selezionato?",
                description: "Il contatore non sarà più disponibile per nuove operazioni. L'operazione è irreversibile.",
                confirm: "Disattiva",
                cancel: "Annulla",
              },
              deactivateSuccess: "Contatore disattivato correttamente.",
              showDeactivated: "Mostra disattivati",
              import: {
                pageTitle: "Importa contatori e chiavi",
                back: "Torna al catalogo contatori",
                importAction: "Importa da CSV",
                template: "Scarica template CSV",
                type: { meters: "Contatori", keys: "Chiavi wM-Bus" },
                typeLabel: "Tipo di file da importare",
                unit: {
                  meters_one: "contatore",
                  meters_other: "contatori",
                  keys_one: "chiave",
                  keys_other: "chiavi",
                },
                subtitle: {
                  lastImport: "Ultimo import: {{date}} · {{rows}} {{unit}}",
                  lastFailed: "Ultimo import: {{date}} · non riuscito",
                  noImport: "Nessun import registrato su questo browser",
                  running: "Import avviato alle {{time}} · non è necessario restare sulla pagina",
                },
                step1: {
                  title: "1 · File CSV",
                  hint: "Formato .csv — il file viene elaborato dal server.",
                  // §14: non esiste un form per-contatore dove appendere questo
                  // avviso — l'import È la via di correzione dell'indirizzo.
                  metersAddressHint: "Nota: correggere qui un indirizzo wM-Bus non aggiorna le campagne già create — il giro sul campo continua a cercare l'indirizzo precedente.",
                },
                dropzone: {
                  title: {
                    meters: "Trascina qui il file CSV dei contatori",
                    keys: "Trascina qui il file CSV delle chiavi wM-Bus",
                  },
                  subtitle: "oppure selezionalo dal disco — il file viene elaborato dal server",
                  action: "Seleziona file CSV",
                  checking: "Analisi del file…",
                  specs: {
                    delimiter: "separatore ;",
                    maxSize: "max {{size}}",
                  },
                },
                file: {
                  rows_one: "{{rows}} riga",
                  rows_other: "{{rows}} righe",
                  replace: "Sostituisci",
                  remove: "Rimuovi",
                },
                step2: {
                  title: "2 · Controllo del file",
                  disabledHint: "Disponibile dopo la selezione del file.",
                  badgeOk: "Pre-check superato",
                  badgeFailed: "Pre-check non superato",
                  metrics: {
                    rows: "Righe stimate",
                    columns: "Colonne rilevate",
                    size: "Dimensione",
                  },
                  columnsValue: "{{present}}/{{total}}",
                  serverNote: "Il controllo qui è locale (estensione, dimensione, intestazione): le righe non valide vengono determinate dal server durante l'import.",
                  cancel: "Annulla",
                  start: "Importa",
                  startWithRows_one: "Importa ~{{rows}} riga",
                  startWithRows_other: "Importa ~{{rows}} righe",
                },
                precheck: {
                  extension: "Il file deve avere estensione .csv.",
                  empty: "Il file è vuoto.",
                  tooLarge: "Il file supera il limite di {{size}} accettato dal server.",
                  missingHeaders: "Colonne mancanti nell'intestazione: {{columns}}.",
                  staleAddressColumnMeters: "La colonna \"{{column}}\" ha il nome vecchio: rinominala in \"wmbusAddress\". Lascia la cella vuota per un contatore senza modulo radio. Nessuna riga è stata importata.",
                  staleAddressColumnKeys: "La colonna \"{{column}}\" ha il nome vecchio: rinominala in \"wmbusAddress\". Su questo file l'indirizzo è obbligatorio per ogni riga: nessuna cella vuota. Nessuna riga è stata importata.",
                  // Non «manca la colonna»: la colonna c'è, il nome no. Dire
                  // «mancante» manda l'operatore a cercare quello che ha già.
                  misspelledColumn: "La colonna \"{{found}}\" non è riconosciuta: deve chiamarsi esattamente \"{{expected}}\". Il confronto ignora le maiuscole, ma non trattini, underscore e spazi. Nessuna riga è stata importata.",
                  // Il carattere non si mostra grezzo: un tabulatore a schermo è
                  // invisibile e l'operatore leggerebbe una frase mutila.
                  wrongSeparator: "Il file usa {{separator}} per separare le colonne: deve usare il punto e virgola. Modifica e salva con \";\" e ripeti l'importazione. Nessuna riga è stata importata.",
                  separators: {
                    comma: "la virgola",
                    tab: "il tabulatore",
                    pipe: "la barra verticale",
                  },
                  readError: "Non è stato possibile leggere il file.",
                },
                progress: {
                  badge: "Import in corso",
                  backToCatalog: "Torna al catalogo",
                  uploading: "Caricamento del file…",
                  processing: "Elaborazione in corso…",
                  processed_one: "{{processed}} riga elaborata",
                  processed_other: "{{processed}} righe elaborate",
                  processedOfTotal_one: "{{processed}} / {{total}} riga",
                  processedOfTotal_other: "{{processed}} / {{total}} righe",
                  processedOfEstimate_one: "{{processed}} / ~{{total}} riga",
                  processedOfEstimate_other: "{{processed}} / ~{{total}} righe",
                  remaining: "rimanenti ~{{eta}}",
                  updatedAgo: "aggiornato {{ago}}",
                  note: "Avanzamento richiesto al server ogni 1,5 s. Puoi chiudere la pagina: l'import continua e lo ritrovi nello storico.",
                  stalledTitle: "Avanzamento non disponibile",
                  stalledDescription: "Il server non risponde alle richieste di stato. L'import continua lato server: riprova a monitorarlo o torna al catalogo.",
                  retry: "Riprova",
                  metrics: {
                    processed: "Righe processate",
                    // Stesso contatore (job.succeeded) e stessa etichetta della
                    // barra d'esito: con l'upsert include le invariate.
                    succeeded: "Riuscite",
                    failed: "Scartate",
                    remaining: "Rimanenti",
                  },
                  rejectedNote_one: "La riga scartata finora è già nel report scarti.",
                  rejectedNote_other: "Le {{rejected}} righe scartate finora sono già nel report scarti.",
                  noRejectedNote: "Nessuna riga scartata finora.",
                },
                eta: {
                  hoursMinutes: "{{hours}} h {{minutes}} min",
                  minutesSeconds: "{{minutes}} min {{seconds}} s",
                  seconds: "{{seconds}} s",
                  ago: "{{seconds}} s fa",
                  agoNow: "ora",
                },
                phases: {
                  title: "Fasi",
                  upload: "File caricato",
                  precheck_one: "Pre-check — {{rows}} riga stimata",
                  precheck_other: "Pre-check — {{rows}} righe stimate",
                  precheckNoRows: "Pre-check superato",
                  write: "Scrittura su database",
                  report: "Report finale e log",
                },
                details: {
                  title: "Dettagli operazione",
                  startedBy: "Avviato da",
                  startedAt: "Ora avvio",
                  jobId: "ID operazione",
                },
                errorsCard: {
                  title: "Errori",
                  empty: "Nessuna riga scartata.",
                },
                format: {
                  title: "Formato richiesto",
                  // Nomi come nella tabella del catalogo contatori: $t() li tiene
                  // allineati alle etichette usate lì.
                  columns: {
                    collectionMode: "Modalità di raccolta (DRIVEBY o MANUAL)",
                    wmbusAddress: "$t(models.meter.wmbusAddress)",
                    vendor: "$t(models.meterModel.vendor)",
                    modelCode: "$t(models.meter.modelId)",
                    serialNumber: "$t(models.meter.serialNumber)",
                    latitude: "$t(models.meter.latitude)",
                    longitude: "$t(models.meter.longitude)",
                    address: "$t(models.meter.address)",
                    notes: "$t(models.meter.notes)",
                    wmbusKey: "Chiave wM-Bus",
                    // Il codice del percorso, non il nome né l'id della zona:
                    // stessa etichetta del form zona, con il contesto in coda.
                    zoneExternalRef: "$t(models.zone.zoneExternalRef) (zona)",
                  },
                  required: "obbligatorio",
                  optional: "opzionale",
                  detected: "rilevata",
                  absent: "assente",
                  // Terza tag, distinta da "assente": la colonna c'è, solo con un
                  // nome diverso da quello atteso (rinominata o quasi-corretta).
                  misspelled: "non riconosciuta",
                  note: "Separatore ; · codifica UTF-8 · prima riga di intestazione obbligatoria.",
                  // §13: la semantica della cella vuota, per kind e per colonna.
                  // Anche la nota GENERALE è per-kind: quella dei contatori
                  // parla di opzionali e collectionMode, che nel file chiavi
                  // non esistono — là entrambe le colonne sono obbligatorie.
                  emptyCellNote: {
                    meters: "Cella vuota: nelle colonne obbligatorie respinge la riga — un CSV di correzione deve ripetere anche i campi che non cambiano. Nelle opzionali significa «non fornito», mai «cancella» — unica eccezione wmbusAddress, governata da collectionMode (v. sopra).",
                    keys: "Cella vuota: respinge la riga — entrambe le colonne sono obbligatorie nel file chiavi.",
                  },
                  emptyCell: {
                    meters: {
                      wmbusAddress: "Vuota: con DRIVEBY la riga è respinta; con MANUAL l'indirizzo viene azzerato — ma se il contatore ha una chiave corrente la riga è respinta e nulla cambia.",
                      notes: "Vuota: le note restano quelle che erano. Cancellare una nota non è previsto in questa release.",
                      wmbusKey: "Vuota: la chiave resta quella che era. Per revocarla si usa la pagina delle chiavi del contatore.",
                      zoneExternalRef: "Vuota: la zona resta quella che era. Per disassegnare si usa la pagina della zona.",
                    },
                  },
                  noteOk: "Tutte le colonne obbligatorie sono presenti nell'intestazione del file.",
                  noteMissing: "Colonne obbligatorie mancanti: {{columns}}.",
                  noteMisspelled: "La colonna \"{{expected}}\" è nel file, ma con il nome \"{{found}}\": rinominala perché venga riconosciuta.",
                },
                history: {
                  title: "Storico import",
                  meta: "solo questo browser",
                  empty: "Nessun import registrato su questo browser.",
                  colDate: "Data",
                  colFile: "File",
                  colType: "Tipo",
                  colImported: "Riuscite",
                  colRejected: "Scartate",
                  colStatus: "Stato",
                  colUser: "Utente",
                  status: {
                    PENDING: "In attesa",
                    RUNNING: "In corso",
                    COMPLETED: "Completato",
                    FAILED: "Fallito",
                  },
                },
                result: {
                  ariaLabel: "Riepilogo import",
                  badgeCompleted: "Completato",
                  badgeNoImports: "Nessun import",
                  badgeFailed: "Fallito",
                  failedTitle: "Importazione fallita",
                  // Ramo senza `fatalError`: il server non ha indicato un motivo,
                  // quindi non è un problema del file e riprovare ha senso. Le
                  // cause di intestazione e separatore portano il proprio testo.
                  failedDescription: "L'elaborazione del file non è riuscita e nessuna riga è stata importata. Il server non ha indicato un motivo: riprova, e se si ripete segnala all'assistenza l'ID dell'operazione.",
                  // «riuscite», non «importate»: job.succeeded include le
                  // righe invariate (stessa ragione del rename in «Riuscite»).
                  failedDescriptionPartial: "L'elaborazione del file si è interrotta con un errore: le righe già riuscite restano in catalogo. Verifica i conteggi qui sotto prima di ricaricare il file.",
                  processed: "Processate",
                  // «Riuscite», non «Importate»: dall'upsert la somma include
                  // le righe invariate, che importate non sono.
                  succeeded: "Riuscite",
                  failed: "Scartate",
                  duration: "Durata",
                  downloadRejected: "Scarica report scarti",
                  downloadRejectedKeysWarning: "Il report degli scarti contiene le chiavi wM-Bus in chiaro: conservalo come dato sensibile.",
                  downloadError: "Download del report non riuscito.",
                  newImport: "Nuovo import",
                  announcementDone: "Import completato. Righe riuscite: {{imported}}. Righe scartate: {{rejected}}.",
                  announcementFailed: "Import non riuscito. Righe riuscite: {{imported}}. Righe scartate: {{rejected}}.",
                  // §14: agganciato a factCounts.WMBUS_ADDRESS_CHANGED. La copy
                  // NON dice «le letture diventano indecifrabili» — la
                  // decodifica risolve le chiavi per meter_id: a rompersi è
                  // solo la cattura sul campo.
                  // §12: ricaricare le STESSE chiavi non è più un rimedio ai
                  // decode falliti. Se il materiale è identico,
                  // MeterKeyRowImporter.apply ritorna unchanged() PRIMA di ogni
                  // outbox.publish: nessun evento, nessuna decodifica riavviata.
                  // Solo sul tab chiavi: sul tab contatori la condizione non è
                  // derivabile dagli outcomeCounts, perché una cella wmbusKey
                  // identica non pubblica evento ma la riga può risultare
                  // UPDATED per altri motivi.
                  keysUnchangedNote: {
                    title_one: "1 chiave era già quella corrente",
                    title_other: "{{count}} chiavi erano già quelle correnti",
                    description: "Per quelle righe non è stata fatta alcuna modifica, e nessun nuovo tentativo di decodifica è stato avviato: ricaricare lo stesso file non fa ripartire le letture non decodificate. Per ritentarle usa «Re-decodifica» dal giro o dalla lettura; una chiave davvero nuova riavvia la decodifica da sé.",
                  },
                  // §14: i giri conservano una COPIA dell'indirizzo presa allo
                  // split e nessuno la aggiorna — va detto il perché, o chi conosce
                  // la chiave di business (vendor/modelCode/seriale) crede che
                  // l'avviso sia superato. Si rompe solo la cattura via radio.
                  addressChangedWarning: {
                    title_one: "Questo file ha corretto l'indirizzo wM-Bus di 1 contatore.",
                    title_other: "Questo file ha corretto l'indirizzo wM-Bus di {{count}} contatori.",
                    description: "I giri delle campagne già create conservano una copia dell'indirizzo presa al momento della creazione: per questi contatori la lettura via radio non riconoscerà il telegramma, mentre la lettura manuale resta possibile e le letture già raccolte non cambiano. Se uno di essi è in una campagna non ancora conclusa, per leggerlo via radio bisogna annullarla e ricrearla.",
                    action: "Vai alle campagne in corso",
                  },
                  // Livello 1 (§8): esito riga, somma = Riuscite. Le etichette
                  // divergono per kind: sull'import chiavi UPDATED è una
                  // rotazione e UNCHANGED il ricarico a vuoto del §12.
                  outcomes: {
                    title: "Esito righe",
                    meters: { CREATED: "Create", UPDATED: "Aggiornate", UNCHANGED: "Invariate" },
                    keys: { CREATED: "Registrate", UPDATED: "Ruotate", UNCHANGED: "Invariate" },
                  },
                  // Livello 2 (§8): fatti sovrapponibili — NON sommano, e non
                  // sono «di cui»: un fatto committato può appartenere a una
                  // riga finita fra le scartate.
                  facts: {
                    title: "Modifiche rilevate",
                    overlapNote: "una riga può comparire in più voci",
                    WMBUS_ADDRESS_ADDED: "Indirizzi aggiunti",
                    WMBUS_ADDRESS_CHANGED: "Indirizzi cambiati",
                    DEMOTED_TO_MANUAL: "Declassati a manuale",
                    ZONE_ASSIGNED: "Zone assegnate",
                    ZONE_MOVED: "Zone spostate",
                    // Conta anche la PRIMA chiave di un contatore che esisteva
                    // senza: entrambi i rami di applyKey arrivano allo stesso
                    // fatto. Non «installate o ruotate», però: un contatore
                    // NUOVO che arriva con chiave non emette alcun fatto
                    // (register() aggiunge solo ZONE_ASSIGNED /
                    // RECREATED_FROM_DEACTIVATED), quindi un file fresco
                    // mostrerebbe 0 contro N chiavi installate. Lo scope resta
                    // «contatori che c'erano già», ed è ciò che l'etichetta dice.
                    KEY_ROTATED: "Chiavi cambiate",
                    RECREATED_FROM_DEACTIVATED: "Ricreati da disattivati",
                  },
                },
                errors: {
                  MISSING_REQUIRED_FIELD: "Campo obbligatorio mancante.",
                  FIELD_TOO_LONG: "Valore troppo lungo.",
                  INVALID_FORMAT: "Formato non valido.",
                  DUPLICATE_ROW_IN_FILE: "Riga duplicata nel file.",
                  INTERNAL_ERROR: "Errore interno durante l'elaborazione.",
                  DB_CONSTRAINT_VIOLATION: "Riga respinta da un vincolo del database.",
                  MODEL_NOT_FOUND: "Modello non trovato in catalogo.",
                  // METER_ALREADY_EXISTS e METER_SERIAL_ALREADY_EXISTS non sono
                  // più raggiungibili dall'import (che è un upsert): restano
                  // per i job pre-upsert ancora consultabili. Non usarle come
                  // modello per testi nuovi.
                  METER_ALREADY_EXISTS: "Contatore già esistente.",
                  METER_NOT_FOUND: "Contatore non trovato.",
                  INVALID_KEY_FORMAT: "Formato chiave wM-Bus non valido.",
                  METER_SERIAL_ALREADY_EXISTS: "Numero di serie già usato per questo modello.",
                  KEY_ON_KEYLESS_METER: "Chiave wM-Bus su un contatore senza modulo radio.",
                  // I sette del rilascio meter-zone-ownership (delta §7).
                  ZONE_NOT_FOUND: "Codice percorso inesistente.",
                  ZONE_RETIRED: "Codice percorso di una zona ritirata.",
                  WMBUS_ADDRESS_ALREADY_IN_USE: "Indirizzo wM-Bus già occupato.",
                  WMBUS_ADDRESS_REQUIRED_FOR_DRIVEBY: "Lettura radio senza indirizzo wM-Bus.",
                  COLLECTION_MODE_CONTRADICTS_ADDRESS: "Modalità manuale con indirizzo wM-Bus.",
                  KEY_PRESENT_CANNOT_DEMOTE: "Chiave presente: passaggio a manuale negato.",
                  WMBUS_ADDRESS_MOVE_NOT_APPLIED: "Spostamento d'indirizzo non applicato.",
                },
                hints: {
                  MISSING_REQUIRED_FIELD: "Compila il campo: è obbligatorio.",
                  FIELD_TOO_LONG: "Accorcia il valore: supera la lunghezza massima ammessa.",
                  INVALID_FORMAT: "Correggi il formato dei valori della riga.",
                  DUPLICATE_ROW_IN_FILE: "Rimuovi la riga duplicata dal file.",
                  INTERNAL_ERROR: "Errore interno: riprova, o segnala l'ID operazione all'assistenza.",
                  // Il nome del vincolo (l'unico dato per l'assistenza) arriva
                  // in `{{message}}`; dal 2026-08-03 il file scarti lo porta
                  // anche nella colonna `dettaglio`.
                  DB_CONSTRAINT_VIOLATION: "Ricarica solo questa riga: un'altra registrazione può averla anticipata mentre l'import era in corso. Se si ripete, segnala all'assistenza — {{message}}",
                  // Due cause sotto lo stesso codice, e la seconda è quella
                  // portante. (a) il confronto col catalogo è carattere per
                  // carattere, maiuscole comprese: VendorModelKey è un record
                  // senza normalizzazione, gli spazi ai bordi li toglie map().
                  // (b) il modello può essere scritto ESATTAMENTE giusto ma non
                  // più ACTIVE: l'indice si costruisce da models.listActive() e
                  // il browse tenant è ACTIVE-only, quindi è invisibile a chi
                  // cerca l'errore. Senza (b) la clausola sul case manda a
                  // caccia di un refuso che non c'è.
                  // Il case NON va chiesto al backend: §18(a) lo registra come
                  // decisione aperta, e allinearlo costerebbe un ottavo codice.
                  MODEL_NOT_FOUND: "Nessun modello attivo con questo produttore e codice: il confronto è identico carattere per carattere, maiuscole comprese. Se la grafia è giusta, il modello può essere stato disattivato — in quel caso non compare più in catalogo.",
                  METER_ALREADY_EXISTS: "Il contatore è già in catalogo: rimuovi la riga, o importa solo la chiave.",
                  METER_NOT_FOUND: "Registra prima il contatore: qui si importano solo le chiavi.",
                  // Niente «senza separatori»: HexKeyParser strippa «-», «:» e
                  // spazi da sé — lo stesso testo di aiuto della modale chiave.
                  INVALID_KEY_FORMAT: "Usa 32 o 64 caratteri esadecimali (separatori «-», «:» o spazi tollerati).",
                  METER_SERIAL_ALREADY_EXISTS: "Correggi il numero di serie, o disattiva il contatore esistente se questo lo sostituisce.",
                  // Riscritto col piano B: il codice resta raggiungibile solo da
                  // una riga MANUAL con chiave, e il vecchio consiglio («indica
                  // il suo indirizzo») produrrebbe COLLECTION_MODE_CONTRADICTS_ADDRESS.
                  KEY_ON_KEYLESS_METER: "Togli la chiave dalla riga, oppure imposta collectionMode a DRIVEBY e valorizza wmbusAddress.",
                  // L'hint nomina il CODICE del percorso (la colonna che
                  // l'operatore deve controllare), mai il nome della zona.
                  ZONE_NOT_FOUND: "Nessuna zona ha questo codice percorso: controlla la colonna zoneExternalRef. Il confronto è identico carattere per carattere, maiuscole comprese.",
                  // Diagnosi diversa da «non esiste» — e niente «riattiva la
                  // zona»: un endpoint di riattivazione non esiste.
                  ZONE_RETIRED: "Il codice percorso esisteva, ma la zona è stata ritirata: correggi la colonna zoneExternalRef, oppure crea una zona nuova con questo codice.",
                  // Tre cause sotto lo stesso codice: catalogo, doppione nello
                  // stesso file, creazione su un indirizzo liberato solo dopo.
                  WMBUS_ADDRESS_ALREADY_IN_USE: "Verifica la colonna wmbusAddress: l'indirizzo appartiene a un altro contatore attivo del catalogo, oppure un'altra riga di questo stesso file lo rivendica, oppure è una riga di creazione su un indirizzo che il file libera solo più avanti.",
                  WMBUS_ADDRESS_REQUIRED_FOR_DRIVEBY: "La riga chiede lettura radio senza fornire l'indirizzo: valorizza wmbusAddress, oppure imposta collectionMode a MANUAL.",
                  COLLECTION_MODE_CONTRADICTS_ADDRESS: "Le due colonne si contraddicono: svuota wmbusAddress, oppure imposta collectionMode a DRIVEBY.",
                  KEY_PRESENT_CANNOT_DEMOTE: "Il contatore ha una chiave corrente: revocala dalla pagina delle chiavi, poi ripeti l'import con MANUAL. Intanto nulla è stato cambiato.",
                  // NON è un errore di questa riga, e il ricarico secco non è
                  // sempre la cura: il backend scrive due messaggi diversi sotto
                  // questo codice — il dettaglio è la parte azionabile.
                  // «un'altra riga» è solo la causa frequente: il backend
                  // scrive due messaggi sotto questo codice (vincolo violato da
                  // un'altra riga, oppure guasto non attribuibile) — la colpa
                  // la dice il dettaglio, non questo testo.
                  WMBUS_ADDRESS_MOVE_NOT_APPLIED: "Non è un errore di questa riga: l'applicazione collettiva degli spostamenti d'indirizzo del file è fallita. Dettaglio — {{message}}",
                },
                // Codici di FILE (`fatalError.code`): il file è respinto in
                // blocco, zero righe importate. Normalmente il pre-check li
                // anticipa; questo testo è la via che resta per un file caricato
                // da un altro client.
                fatalErrors: {
                  UNSUPPORTED_CSV_SEPARATOR: "Il file non usa il punto e virgola come separatore di colonna. Risalvalo con «;» e ricaricalo: nessuna riga è stata importata.",
                  // Il solo dei tre il cui specifico varia: il messaggio del
                  // backend nomina la colonna canonica, il nostro testo no.
                  MISSING_REQUIRED_HEADER_COLUMN: "L'intestazione non porta una colonna obbligatoria sotto il nome esatto richiesto. Correggila e ricarica il file: nessuna riga è stata importata. Dettaglio — {{message}}",
                  STALE_HEADER_SECONDARY_ADDRESS: "L'intestazione chiama ancora la colonna dell'indirizzo «secondaryAddress»: rinominala in «wmbusAddress» e ricarica il file. Nessuna riga è stata importata.",
                },
                rejectedCard: {
                  title: "Righe scartate",
                  badge: "{{failed}} su {{processed}}",
                  allSameError: "Tutte con lo stesso errore: {{error}}",
                  filterAll: "Tutti gli errori",
                  filterSelected: "{{label}} ({{n}})",
                  copy: "Copia elenco",
                  copied: "Copiato",
                  copyError: "Copia negli appunti non riuscita.",
                  // Marcatore in testa al TSV copiato: senza, 100 righe incollate in un
                  // foglio di calcolo sembrano l'elenco completo.
                  copyNote: "Elenco non completo: campione di {{shown}} righe scartate su {{total}}.",
                  emptyUnavailable: "Il dettaglio delle righe scartate non è disponibile: l'elaborazione si è interrotta prima di produrlo.",
                  footerComplete_one: "{{rows}} riga su {{rows}} · elenco completo, nessuna riga nascosta",
                  footerComplete_other: "{{rows}} righe su {{rows}} · elenco completo, nessuna riga nascosta",
                  footerTruncated: "Prime {{shown}} righe di {{total}} · l'elenco completo è nel report scarti",
                  footerTruncatedNoReport: "Prime {{shown}} righe di {{total}}",
                  // Gli scarti di fine file non finiscono mai nel report
                  // scaricabile: esistono solo nel campione inline.
                  footerMoveNotApplied: "le righe «Spostamento d'indirizzo non applicato» compaiono solo in questo elenco, non nel report scarti",
                  footerUnavailable_one: "Nessun dettaglio disponibile per la riga scartata.",
                  footerUnavailable_other: "Nessun dettaglio disponibile per le {{total}} righe scartate.",
                  footerFiltered: "{{visible}} di {{total}} righe · filtro: {{label}}",
                  // Coda del footer filtrato quando il campione è troncato: il filtro
                  // riferisce i suoi conteggi al campione, non a tutti gli scarti.
                  footerSampleScope: "campione di {{shown}} su {{total}}",
                  downloadLink: "Scarica report scarti (CSV)",
                  noneTitle: "Nessuna riga scartata",
                  // «elaborata», non «importata»: dall'upsert il file può essere
                  // fatto di sole righe invariate.
                  noneDescription_one: "L'unica riga del file è stata elaborata senza scarti.",
                  noneDescription_other: "Tutte le {{rows}} righe del file sono state elaborate senza scarti.",
                  noneDescriptionEmptyFile: "Il file non conteneva righe da importare.",
                  noneDescriptionFailedNone: "L'elaborazione si è interrotta prima di importare righe.",
                  noneDescriptionFailedPartial_one: "L'elaborazione si è interrotta: {{rows}} riga riuscita resta in catalogo.",
                  noneDescriptionFailedPartial_other: "L'elaborazione si è interrotta: {{rows}} righe riuscite restano in catalogo.",
                  colLine: "Riga",
                  colField: "Colonna",
                  colHint: "Come correggere",
                },
              },
              viewMode: {
                table: "Tabella",
                map: "Mappa",
              },
              map: {
                filterTitle: "Filtra su mappa",
                drawMode: {
                  polygon: "Poligono",
                  circle: "Cerchio",
                },
                polygonHint: "Disegna un poligono con la toolbar in alto a sinistra, poi applica il filtro alla tabella.",
                circleHint: "Disegna un cerchio (trascina dal centro) con la toolbar in alto a sinistra per filtrare i contatori nel raggio.",
                center: "Centro (lat, lng)",
                radius: "Raggio",
                drawCircle: "Disegna cerchio",
                editShape: "Modifica forma",
                dragShape: "Sposta forma",
                deleteShape: "Elimina forma",
                cancel: "Annulla",
                finish: "Termina",
                applyFilter: "Applica filtro",
                clearFilter: "Rimuovi filtro",
                filterApplied: "Filtro spaziale applicato.",
                filterActive: "Filtro mappa attivo",
                legendTitle: "Contatori",
                emptyMeters: "Nessun contatore con coordinate da mostrare.",
                loadError: "Errore nel caricamento dei contatori per la mappa.",
              },
            },
            zone: {
              relationTitle: "Visualizza Zone",
              tableTitle: "Gestione Zone",
              singleElement: "Zona",
              name: "Nome",
              // Il codice con cui il cliente identifica il percorso: è la chiave
              // di binding della colonna `zoneExternalRef` dei file di import.
              zoneExternalRef: "Codice percorso",
              zoneExternalRefHelp: "Codice con cui il committente identifica il percorso (max 256 caratteri ASCII). Cambiarlo invalida i file di import che usano il codice precedente nella colonna zoneExternalRef.",
              zoneExternalRefCharset: "Solo caratteri ASCII stampabili (niente accenti né caratteri di controllo).",
              zoneExternalRefBlank: "Il codice percorso non può essere fatto di soli spazi.",
              // Errori di salvataggio che il generico errors.backend.* non sa
              // attribuire al campo giusto (v. zoneSaveErrors.ts).
              errors: {
                codeInUse: "Codice percorso già usato da una zona attiva: sceglierne un altro, o ritirare prima la zona che lo usa.",
                nameInUse: "Esiste già una zona con questo nome.",
                codeCharset: "Il codice percorso contiene caratteri non ammessi: solo ASCII stampabile (attenzione ai caratteri invisibili da copia-incolla).",
                editRetired: "La zona è ritirata e non è più modificabile. Per riusare il suo codice percorso, creare una zona nuova.",
              },
              description: "Descrizione",
              fallbackTeamId: "Team di fallback",
              fallbackTeamIdHelpNoAccess: "Non sei autorizzato a sfogliare i team — inserire l'ID del team di fallback.",
              status: {
                title: "Stato",
                ACTIVE: "Attiva",
                RETIRED: "Ritirata",
              },
              retiredAt: "Data ritiro",
              createdAt: "Data creazione",
              updatedAt: "Ultima modifica",
              retire: "Ritira",
              retireConfirm: {
                title: "Ritirare la zona selezionata?",
                description: "La zona non sarà più disponibile per nuove assegnazioni. L'operazione è irreversibile.",
                confirm: "Ritira",
                cancel: "Annulla",
              },
              retireSuccess: "Zona ritirata correttamente.",
              showRetired: "Mostra ritirate",
              manageMeters: "Gestisci contatori",
              manageAreas: "Gestisci aree",
            },
            area: {
              relationTitle: "Visualizza Aree",
              tableTitle: "Gestione Aree",
              singleElement: "Area",
              name: "Nome",
              zoneId: "Zona",
              teamId: "Team incaricato",
              teamIdHelpNoAccess: "Non sei autorizzato a sfogliare i team — inserire l'ID del team.",
              polygon: "Poligono",
              polygonMinPoints: "Il poligono deve avere almeno 3 vertici. Disegnalo sulla mappa.",
              status: {
                title: "Stato",
                ACTIVE: "Attiva",
                RETIRED: "Ritirata",
              },
              retiredAt: "Data ritiro",
              createdAt: "Data creazione",
              updatedAt: "Ultima modifica",
              retire: "Ritira",
              retireConfirm: {
                title: "Ritirare l'area selezionata?",
                description: "L'area non sarà più disponibile per nuove assegnazioni. L'operazione è irreversibile.",
                confirm: "Ritira",
                cancel: "Annulla",
              },
              retireSuccess: "Area ritirata correttamente.",
              retiredNotEditable: "Le aree ritirate non sono modificabili",
              showRetired: "Mostra ritirate",
              viewMode: {
                table: "Tabella",
                map: "Mappa",
              },
              polygonField: {
                editFromMap: "Disegna o modifica il poligono direttamente sulla mappa.",
                points_one: "{{count}} punto definito",
                points_other: "{{count}} punti definiti",
                emptyDraft: "Nessun punto disegnato. Usa la toolbar di disegno sulla mappa.",
              },
              miniMap: {
                title: "Posizione",
              },
              byZone: {
                pageTitle: "Aree della zona — {{zoneName}}",
                back: "Torna alle zone",
              },
              map: {
                drawPolygon: "Disegna poligono",
                editPolygon: "Modifica poligono",
                deletePolygon: "Elimina poligono",
                snapToggle: "Aggancio ai confini: i vertici si agganciano ai bordi delle aree esistenti. Clicca per attivare/disattivare · tieni premuto Alt per sospenderlo al volo",
                createArea: "Crea area (disegna il poligono)",
                createFirstArea: "Crea la prima area",
                drawHint: "Clicca sulla mappa per posizionare i vertici · clicca sul primo punto (o doppio clic) per chiudere il poligono · tieni premuto Alt per un punto libero (niente aggancio)",
                drawHintSnapOff: "Clicca sulla mappa per posizionare i vertici · clicca sul primo punto (o doppio clic) per chiudere il poligono · aggancio ai confini disattivato",
                firstVertex: "Clicca per inserire il primo vertice",
                continueLine: "Clicca per inserire un altro vertice",
                finishPoly: "Clicca sul primo vertice per chiudere il poligono",
                cancel: "Annulla",
                finish: "Termina",
                removeLastVertex: "Rimuovi l'ultimo vertice",
                emptyAreas: "Nessuna area da mostrare.",
                loadError: "Errore nel caricamento delle aree per la mappa.",
                showMeters: "Mostra contatori",
                metersLegendTitle: "Contatori",
                zonesLegendTitle: "Zone",
                loadErrorMeters: "Errore nel caricamento dei contatori.",
                meterPopup: {
                  address: "Indirizzo",
                  status: "Stato",
                },
                discardChanges: {
                  title: "Abbandonare le modifiche?",
                  description: "Hai modifiche non salvate al poligono. Vuoi davvero cambiare selezione?",
                  confirm: "Abbandona",
                  cancel: "Continua a modificare",
                },
              },
            },
            zoneMeter: {
              pageTitle: "Contatori della zona — {{zoneName}}",
              tableTitle: "Contatori assegnati",
              back: "Torna alle zone",
              empty: "Nessun contatore assegnato a questa zona.",
              assign: "Assegna contatore",
              cannotAssignRetired: "Non è possibile gestire contatori per una zona ritirata.",
              zoneAssignedAt: "Data assegnazione",
              assignBulkSuccess: "Contatori assegnati: {{assigned}} · spostati da un'altra zona: {{moved}}.",
              assignBulkUnchanged: "Già in questa zona: {{count}}.",
              assignManyFailed: "Impossibile assegnare i contatori selezionati ({{count}}).",
              allAlreadyInZone: "I contatori selezionati sono già membri di questa zona.",
              unassignManySuccess_one: "1 contatore rimosso dalla zona.",
              unassignManySuccess_other: "{{count}} contatori rimossi dalla zona.",
              unassignNothingToDo: "Nessun contatore rimosso: la selezione non risultava più assegnata a questa zona.",
              unassignManyFailed: "Impossibile rimuovere i contatori selezionati ({{count}}).",
              emptyZone: "Svuota la zona",
              emptyZoneConfirm: {
                title: "Rimuovere tutti i contatori dalla zona?",
                description: "Tutti i contatori assegnati a «{{zoneName}}» resteranno nel catalogo ma senza percorso, e andranno riassegnati prima di poter entrare in una campagna. Sono compresi gli eventuali contatori disattivati, che conservano la zona ma non compaiono in questo elenco: il totale rimosso può quindi superare le righe che vedi.",
                confirm: "Svuota",
                cancel: "Annulla",
              },
              emptyZoneEmpty: "La zona non aveva contatori assegnati.",
              emptyZoneSuccess_one: "1 contatore rimosso dalla zona.",
              emptyZoneSuccess_other: "{{count}} contatori rimossi dalla zona.",
              emptyZoneFailed: "Impossibile svuotare la zona.",
              modal: {
                title: "Assegna contatori alla zona {{zoneName}}",
                selectedCount: "{{count}} selezionati",
                alreadyInThisZone: "già in questa zona",
                confirm: "Assegna",
                cancel: "Annulla",
                reassignMany: {
                  title: "Riassegnare i contatori già in altre zone?",
                  intro: "Alcuni contatori selezionati sono già membri di un'altra zona e verranno spostati in {{toZone}}:",
                  // Aggregato per zona di partenza: con qualche migliaio di
                  // contatori l'elenco riga per riga era illeggibile.
                  item_one: "1 contatore attualmente nella zona {{fromZone}}",
                  item_other: "{{count}} contatori attualmente nella zona {{fromZone}}",
                  confirm: "Riassegna e assegna",
                  cancel: "Annulla",
                },
                retryWithout: {
                  title: "Riprovare senza i contatori non più disponibili?",
                  intro: "Contatori selezionati non più disponibili (disattivati o rimossi): {{missing}}. L'assegnazione è stata annullata per intero. Vuoi assegnare i rimanenti ({{remaining}})?",
                  confirm: "Assegna i rimanenti",
                  cancel: "Annulla",
                },
              },
            },
            meterKey: {
              pageTitle: "Chiave wM-Bus — {{wmbusAddress}}",
              pageTitleNoAddress: "Chiave wM-Bus",
              // Perché l'operazione è negata, e come uscirne. La seconda frase
              // diceva che l'indirizzo non si può aggiungere dopo la
              // registrazione: dal piano B è falso — il contatore è mutabile in
              // place e l'import aggiunge l'indirizzo (WMBUS_ADDRESS_ADDED). Il
              // backend ha ritirato la stessa premessa dal proprio messaggio
              // (correzione 2026-08-08). Lasciarla qui manderebbe al workaround
              // distruttivo — disattiva e ri-registra — che crea un contatore
              // DIVERSO, fuori da ogni campagna già creata.
              // `collectionMode=DRIVEBY` non è un dettaglio: una riga MANUAL che
              // porta un indirizzo viene respinta dall'importer.
              noAddressNotice: "Questo contatore non ha un indirizzo wM-Bus: senza modulo radio una chiave non avrebbe alcun uso. L'indirizzo si aggiunge con una riga di import contatori che porti la chiave di business di questo contatore, l'indirizzo e collectionMode=DRIVEBY.",
              // Nomina la destinazione, non l'azione: il click naviga, non
              // aggiunge l'indirizzo.
              noAddressGoToImport: "Vai all'import contatori",
              noAddressDisabled: "Non disponibile: il contatore non ha un indirizzo wM-Bus.",
              back: "Torna ai contatori",
              manageKey: "Gestisci chiave wM-Bus",
              noKey: "Nessuna chiave registrata per questo contatore.",
              registerKey: "Registra chiave",
              rotateKey: "Sostituisci chiave",
              revokeKey: "Revoca chiave",
              registerSuccess: "Chiave registrata correttamente.",
              rotateSuccess: "Chiave sostituita correttamente.",
              revokeSuccess: "Chiave revocata correttamente.",
              tableTitle: "Storico chiavi",
              current: {
                title: "Chiave corrente",
                keyVersion: "Versione",
                createdAt: "Creata il",
                rotatedAt: "Ultima rotazione",
                notRotated: "—",
              },
              history: {
                keyVersion: "Versione",
                createdAt: "Creata il",
                retiredAt: "Ritirata il",
                retireReason: {
                  title: "Motivo",
                  ROTATED: "Sostituita",
                  REVOKED: "Revocata",
                },
                empty: "Nessuna chiave ritirata.",
              },
              modal: {
                // *NoAddress: il separatore del template diventerebbe un doppio
                // trattino se il valore interpolato fosse a sua volta "—".
                register: {
                  title: "Registra chiave wM-Bus — {{meter}}",
                  titleNoAddress: "Registra chiave wM-Bus",
                  confirm: "Registra",
                },
                rotate: {
                  title: "Sostituisci chiave wM-Bus — {{meter}}",
                  titleNoAddress: "Sostituisci chiave wM-Bus",
                  confirm: "Sostituisci",
                },
                plaintextKey: "Chiave (hex)",
                plaintextKeyHelp: "32 o 64 caratteri esadecimali (AES-128 / AES-256). Separatori «-», «:» o spazi tollerati.",
                plaintextKeyPlaceholder: "Es. 1A2B3C4D…",
                cancel: "Annulla",
              },
              revokeConfirm: {
                title: "Revocare la chiave corrente?",
                description: "Operazione irreversibile. Il contatore non avrà più una chiave attiva finché non ne registri una nuova.",
                confirm: "Revoca",
                cancel: "Annulla",
              },
            },
            collectionRequest: {
              tableTitle: "Campagne di raccolta",
              singleElement: "Campagna",
              // WEB-188: il riferimento esterno è il nome della campagna.
              externalRef: "Nome",
              source: "Origine",
              sourceValues: {
                SAC: "SAC",
                WEB: "Web",
              },
              status: "Stato",
              statusValues: {
                RECEIVED: "Ricevuta",
                SPLITTING: "In suddivisione",
                SCHEDULED: "Pianificata",
                IN_PROGRESS: "In corso",
                PARTIALLY_COMPLETED: "Parzialmente completata",
                COMPLETED: "Completata",
                EXPIRED: "Scaduta",
                FAILED_TO_SPLIT: "Suddivisione fallita",
                ABORTED: "Annullata",
              },
              meterCount: "Contatori",
              receivedAt: "Data ricezione",
              validFrom: "Valida dal",
              validTo: "Valida al",
              failureReason: "Motivo errore",
              // Dizionario di splitFailureText: chiavi = famiglia del motivo
              // (testo prima dei ":") più, per SPLIT_FAILED, la reason annidata del
              // ripartitore di territorio. Una chiave assente non è un buco: il
              // mapper ripiega sulla stringa grezza del backend.
              failureReasons: {
                METER_NOT_ACTIVE: "Il contatore {{meterId}} non è un contatore attivo del catalogo: è stato disattivato oppure non esiste più.",
                METER_NOT_IN_ANY_ZONE: "Il contatore {{meterId}} non è assegnato a nessuna zona: assegnalo a un percorso e ricrea la campagna.",
                METER_IN_RETIRED_ZONE: "Il contatore {{meterId}} appartiene a una zona ritirata: riassegnalo a un percorso attivo e ricrea la campagna.",
                METER_NOT_FOUND: "Nessuno dei contatori della campagna è utilizzabile: sono stati disattivati oppure non esistono più nel catalogo.",
                TEAM_NOT_FOUND: "Un team a cui era destinato un percorso della campagna non esiste più.",
                SPLIT_ERROR: "Errore inatteso durante la suddivisione della campagna: segnala il dettaglio tecnico al supporto.",
                STALE_SPLITTING: "La suddivisione è rimasta ferma oltre la soglia di inattività ed è stata interrotta: ricrea la campagna.",
                // Avviso 2026-10-22: tetto di location per giro, rete dello split.
                RUN_TOO_LARGE_one: "La suddivisione non è riuscita: 1 giro supererebbe le {{limit}} location ({{runs}}). Ogni giro può avere al più {{limit}} location: dividi la zona in aree più piccole o dividi la campagna, poi ricrea.",
                RUN_TOO_LARGE_other: "La suddivisione non è riuscita: {{count}} giri supererebbero le {{limit}} location ({{runs}}). Ogni giro può avere al più {{limit}} location: dividi la zona in aree più piccole o dividi la campagna, poi ricrea.",
                RUN_TOO_LARGE_UNPARSED: "La suddivisione non è riuscita: un giro supererebbe il tetto di location. Ogni giro può avere al più il tetto di location: dividi la zona in aree più piccole o dividi la campagna, poi ricrea.",
              },
              notes: "Note",
              abortNote: "Nota di annullamento",
              updatedByActor: "Aggiornata da",
              createdAt: "Data creazione",
              empty: "Nessuna campagna trovata.",
              detailTitle: "Campagna — {{ref}}",
              back: "Torna alle campagne",
              coverageTitle: "Copertura",
              coverageCovered: "coperto",
              viewRuns: "Vedi giri",
              abort: "Annulla campagna",
              // Il perché del bottone disabilitato: la suddivisione sovrascriverebbe
              // l'annullamento, e l'attesa non è indefinita — una suddivisione
              // bloccata viene chiusa da sola entro la soglia di inattività.
              abortBlockedBySplit: "Non disponibile durante la suddivisione: l'esito dell'annullamento andrebbe perso. Attendi che la suddivisione finisca, poi riprova.",
              abortSuccess: "Campagna annullata correttamente.",
              // Usato quando lo stato al click poteva essere a metà suddivisione:
              // l'annullamento è stato REGISTRATO, ma l'esito può ancora essere
              // sovrascritto e nessun istante di lettura lo decide da solo.
              abortAcceptedUnconfirmed: "Annullamento registrato. Se la campagna era in suddivisione, l'esito potrebbe non essere definitivo: verifica lo stato con «Aggiorna».",
              // Impronta di un annullamento sovrascritto dalla suddivisione.
              // Due rami: il rimedio si promette solo dove esiste davvero.
              abortOverwritten: {
                title: "Questa campagna risulta annullata, ma è proseguita lo stesso",
                descriptionRetry: "L'annullamento è stato registrato — la nota qui sotto lo dimostra — ma la suddivisione ne ha sovrascritto l'esito, e i giri sono attivi. Annulla di nuovo: ora la suddivisione è conclusa e l'operazione va a buon fine.",
                description: "L'annullamento è stato registrato — la nota qui sotto lo dimostra — ma la suddivisione ne ha sovrascritto l'esito e la campagna è proseguita fino alla conclusione.",
                tagHint: "Annullamento sovrascritto: questa campagna era stata annullata, ma è proseguita.",
              },
              abortConfirm: {
                title: "Annullare la campagna?",
                description: "La campagna verrà terminata e i suoi giri non ancora conclusi verranno terminati a cascata. L'operazione è irreversibile.",
                confirm: "Annulla campagna",
                cancel: "Indietro",
                notePlaceholder: "Nota (facoltativa)",
              },
              create: "Crea campagna",
              createTitle: "Nuova campagna di raccolta",
              createSuccess: "Campagna creata. Lo split è in corso: usa Aggiorna tra qualche secondo per vedere i giri generati.",
              createForm: {
                confirm: "Crea",
                cancel: "Annulla",
                externalRefPlaceholder: "Obbligatorio — massimo 128 caratteri",
                notesPlaceholder: "Nota facoltativa (max 2000 caratteri)",
                metersLabel: "Contatori",
                selectedCount: "{{count}} selezionati",
                searchPlaceholder: "Cerca per indirizzo wM-Bus…",
                validFromHint: "Predefinita a oggi; può essere pianificata nel futuro.",
                validWindowError: "La data di inizio validità deve essere precedente alla data di fine validità.",
              },
              createPage: {
                title: "Nuova campagna di raccolta",
                back: "Torna alle campagne",
                campaignDataTitle: "Dati campagna",
                metersCardTitle: "Seleziona contatori",
                onlyAssignedHint: "Sono elencati solo i contatori assegnati a un percorso, perché la campagna viene suddivisa per zona: quelli senza zona non compaiono e vanno prima assegnati dalla pagina della zona. Usa il filtro sulla colonna «Zona» per restringere a una o più zone.",
                zoneColumn: "Zona",
                deactivatedSelectedError: "Alcuni contatori selezionati risultano disattivati: sono stati rimossi dalla selezione.",
                selectAllButton: "Seleziona tutti i risultati ({{count}})",
                deselectAllButton: "Deseleziona tutto ({{count}})",
                selectAllTooMany: "Troppi contatori ({{count}}): restringi il filtro (massimo {{max}})",
                selectAllWouldExceed: "La selezione supererebbe il limite di {{max}} contatori",
                selectAllAdded_one: "1 contatore aggiunto alla selezione",
                selectAllAdded_other: "{{count}} contatori aggiunti alla selezione",
                // Avviso 2026-10-22: 422 RUN_TOO_LARGE, un giro oltre il tetto di location.
                runTooLarge: {
                  title_one: "La campagna non è stata creata: 1 giro supererebbe le {{limit}} location",
                  title_other: "La campagna non è stata creata: {{count}} giri supererebbero le {{limit}} location",
                  runLine: "{{label}} — {{meters}} location",
                  openZoneAreas: "Apri le aree della zona",
                  remedy: "Ogni giro può avere al più {{limit}} location: dividi la zona in aree più piccole o dividi la campagna, poi ricrea.",
                },
                // §15: creazione per identità di business (elenco seriali).
                mode: {
                  catalog: "Dal catalogo",
                  serials: "Da elenco seriali",
                },
                serials: {
                  cardTitle: "Elenco seriali",
                  label: "Elenco seriali",
                  placeholder: "Un contatore per riga: seriale, oppure vendor;modelCode;seriale",
                  hint: "Incolla l'elenco (da un foglio o da un altro sistema): un contatore per riga. Formati: «seriale» oppure «vendor;modelCode;seriale» — la risoluzione la fa il server, che segnala i seriali ambigui invece di scegliere a caso.",
                  summary_one: "1 contatore riconosciuto",
                  summary_other: "{{count}} contatori riconosciuti",
                  // Oltre le prime 5 righe mostrate in dettaglio.
                  lineErrorsMore_one: "…e un'altra riga non valida.",
                  lineErrorsMore_other: "…e altre {{count}} righe non valide.",
                  tooMany: "Troppi contatori ({{count}}): massimo {{max}} per campagna.",
                  // Caricamento da file: il CSV non va al backend, viene riversato
                  // nella textarea nel formato del parser (v. meterKeyCsv.ts).
                  file: {
                    action: "Carica da file CSV",
                    loaded: "File «{{name}}» caricato: il contenuto ha sostituito l'elenco.",
                    extension: "Il file deve avere estensione .csv.",
                    tooLarge: "File troppo grande ({{size}}): massimo {{max}}.",
                    readError: "Impossibile leggere il file.",
                    empty: "Il file non contiene righe di contatori.",
                    wrongSeparator: "L'intestazione del file usa {{separator}} come separatore di colonna: serve il punto e virgola («;»).",
                    halfModelHeader: "Le colonne vendor e modelCode vanno fornite insieme: nel file ce n'è una sola.",
                  },
                  lineReason: {
                    halfModel: "riga {{line}}: vendor e modelCode vanno forniti insieme (usa 1 campo o 3 campi)",
                    tooManyFields: "riga {{line}}: troppi campi — usa «seriale» o «vendor;modelCode;seriale»",
                    invalidSerial: "riga {{line}}: valore non valido (solo ASCII stampabile, max 100 caratteri)",
                  },
                  // Recupero dal 422: il backend nomina i colpevoli, il riprova
                  // è una SECONDA chiamata esplicita con la lista potata.
                  recovery: {
                    title: "Il server ha respinto la creazione: nessuna campagna creata",
                    intro: "La creazione è tutto-o-niente: un solo contatore inidoneo la annulla per intero. Contatori segnalati:",
                    // Neutra rispetto alla forma inviata: `missingMeterKeys` porta
                    // sia seriali nudi sia triple, e questa riga le rende entrambe.
                    // Non nominare qui il modello disattivato come causa: sui
                    // seriali nudi è impossibile, e §18(b) è ancora una decisione
                    // aperta lato backend.
                    notFound: "non risolve a nessun contatore attivo — controlla il seriale e, se li hai indicati, vendor e modelCode",
                    ambiguous_one: "ambiguo: 1 contatore con questo seriale",
                    // Niente consiglio di qualificare con vendor;modelCode: una
                    // tripla completa può essere ambigua lo stesso (la risoluzione
                    // ventaglia sui modelli che differiscono solo per maiuscole),
                    // e chi l'aveva già scritta si sentirebbe consigliare ciò che
                    // ha già fatto.
                    ambiguous_other: "ambiguo: {{count}} contatori attivi hanno questo seriale — toglilo dall'elenco o correggi il dato a catalogo",
                    notInActiveZone: "non è assegnato a un percorso attivo — assegnalo dalla pagina della zona",
                    confirm_one: "Crea senza questi (1 rimanente)",
                    confirm_other: "Crea senza questi ({{count}} rimanenti)",
                    cancel: "Annulla",
                    nothingLeft: "Nessun contatore rimarrebbe in campagna: correggi l'elenco e riprova.",
                    // Il backend ha nominato qualcuno che non combacia con nessuna
                    // riga inviata: riprovare manderebbe la stessa lista.
                    noneRemoved: "Nessuna riga dell'elenco corrisponde ai contatori segnalati: correggi l'elenco e riprova.",
                  },
                },
              },
            },
            collectionRun: {
              tableTitle: "Giri di raccolta",
              detailTitle: "Giro — {{label}}",
              singleElement: "Giro",
              label: "Etichetta",
              status: "Stato",
              statusValues: {
                ASSIGNED: "Assegnato",
                ASSIGNED_FUTURE: "Pianificato",
                IN_PROGRESS: "In corso",
                TO_BE_TERMINATED: "Da completare",
                COMPLETED: "Completato",
                TERMINATED: "Terminato",
              },
              toBeTerminatedHint: "Da completare — riapribile dall'operatore finché la finestra è aperta (fino al {{validTo}}).",
              toBeTerminatedHintNoDate: "Da completare — riapribile dall'operatore finché la finestra di validità è aperta.",
              teamId: "Squadra",
              zoneId: "Zona",
              areaId: "Area",
              fallback: "Fallback",
              geoCoverage: {
                label: "Copertura stradale",
                covered: "Coperto",
                outOfCoverage: "Fuori copertura",
                waitingFor: "non percorribile dall'app finché non è attiva una di: {{areas}}",
                noCandidates: "nessuna area copre il giro: verificare le coordinate delle location",
              },
              coveragePct: "Copertura",
              totalLocations: "Location totali",
              expiresAt: "Scadenza",
              startedAt: "Avviato il",
              stoppedAt: "Fermato il",
              finalizedAt: "Finalizzato il",
              terminationReason: "Motivo terminazione",
              terminationValues: {
                COMPLETED_NORMAL: "Completato",
                EXPIRED: "Scaduto",
                EXPIRED_WITH_GAP: "Scaduto con lacune",
                FORCED_BY_ADMIN: "Forzato da admin",
                REQUEST_ABORTED: "Campagna annullata",
              },
              finalizedByActor: "Finalizzato da",
              stoppedByActor: "Fermato da",
              stopNote: "Nota di stop",
              finalizeNote: "Nota di finalizzazione",
              stoppedBy: {
                operator: "Operatore",
                admin: "Admin ({{actor}})",
              },
              coverageTitle: "Copertura",
              coverageTabs: {
                coverage: "Copertura",
                outcome: "Esito",
                decodeFailuresTitle: "Motivi di decodifica fallita",
              },
              requestTitle: "Campagna di origine",
              requestExternalRef: "Nome campagna",
              locationsTitle: "Progresso location",
              empty: "Nessun giro trovato.",
              back: "Torna ai giri",
              backToRequests: "Torna alle campagne",
              finalize: "Finalizza (definitivo)",
              finalizeSuccess: "Giro finalizzato correttamente.",
              finalizeConfirm: {
                title: "Finalizzare forzatamente il giro?",
                description: "Il giro verrà chiuso bypassando il flusso operatore. L'operazione è irreversibile.",
                confirm: "Finalizza",
                cancel: "Indietro",
                notePlaceholder: "Nota (facoltativa)",
              },
              stop: "Ferma il giro (riapribile)",
              stopSuccess: "Giro fermato. Resta riapribile dall'operatore finché la finestra è aperta.",
              lastActivityAt: "Ultima attività",
              stopConfirm: {
                title: "Fermare il giro per conto dell'operatore?",
                description: "Il giro viene fermato come se l'operatore avesse premuto stop sull'applicazione mobile.",
                confirm: "Ferma giro",
                cancel: "Indietro",
                notePlaceholder: "Nota (facoltativa)",
              },
              reDecodeRun: "Re-decodifica giro",
              reDecodeQueued: "{{count}} letture messe in coda per la ri-decodifica.",
              reDecodeConfirm: {
                title: "Re-decodificare le letture del giro?",
                description: "Le letture drive-by selezionate verranno rimesse in coda e ri-decodificate in background (entro ~60s). Lascia vuoto per ri-decodificare tutte quelle non ancora decodate.",
                allNonDecoded: "Tutte le non decodate",
                confirm: "Re-decodifica",
                cancel: "Indietro",
              },
              location: {
                sequence: "N.",
                serialNumber: "Numero di serie",
                wmbusAddress: "Indirizzo wM-Bus",
                address: "Indirizzo",
                readingAt: "Data Lettura",
                resultStatus: "Esito",
                lastReceivedAt: "Ultima ricezione",
                viewReading: "Lettura",
                empty: "Nessuna location per questo giro.",
                // Non sotto readingStateValues: quel blocco contiene i sei valori
                // dell'enum ReadingState, che questi due non sono.
                awaitingBuckets: {
                  AWAITING_DRIVEBY: "Da leggere drive-by",
                  AWAITING_MANUAL: "Da leggere manuale",
                },
              },
              viewMode: {
                table: "Info",
                map: "Mappa",
              },
              map: {
                emptyLocations: "Nessun contatore con coordinate per questa campagna.",
                loadError: "Errore nel caricamento dei contatori della campagna.",
                openReading: "Apri lettura",
                legendTitle: "Esito",
                legendGroups: {
                  read: "Raccolta",
                  inProgress: "In corso",
                  problem: "Con problemi",
                  notCollected: "Non raccolta",
                },
                showAreas: "Aree",
                // Riga aggregata della barra di copertura: è la somma dei due
                // bucket «da leggere», che qui non sono separabili (i conteggi del
                // run non portano l'indirizzo wM-Bus).
                awaiting: "Da leggere",
                awaitingHint: "Somma di «Da leggere drive-by» e «Da leggere manuale»: qui non sono separabili, perché i conteggi del giro non riportano l'indirizzo wM-Bus.",
                toggleResult: "Mostra/nascondi sulla mappa",
                popup: {
                  serialNumber: "Numero di serie",
                  wmbusAddress: "Indirizzo wM-Bus",
                  address: "Indirizzo",
                  latitude: "Latitudine",
                  longitude: "Longitudine",
                  result: "Esito",
                  lastReceived: "Ultima ricezione",
                },
              },
            },
            reading: {
              tableTitle: "Letture",
              tableTitleForRun: "Letture del giro #{{runId}}",
              tableTitleForRequest: "Letture della campagna #{{requestId}}",
              detailTitle: "Dettaglio lettura",
              serialNumber: "Numero di serie",
              openMeter: "Apri nel catalogo contatori",
              openMeterNoPrivilege: "Richiede il ruolo amministratore del catalogo",
              teamId: "Squadra",
              zoneId: "Zona",
              areaId: "Area",
              runId: "Giro",
              requestId: "Campagna",
              meterId: "Contatore",
              searchPlaceholder: {
                requestId: "Cerca per riferimento",
                meterId: "Cerca per numero di serie",
              },
              filters: {
                outcome: "Esito",
                territory: "Territorio",
                run: "Campagna e giro",
                meter: "Contatore",
                clearAll: "Cancella filtri",
              },
              status: "Esito",
              statusValues: {
                READ_DRIVEBY: "Letto (drive-by)",
                READ_MANUAL: "Letto (manuale)",
                NOT_COLLECTED: "Non raccolta",
                NOT_READABLE: "Non leggibile",
              },
              readingState: "Stato lettura",
              readingStateValues: {
                DRIVEBY_DECODED: "Letto drive-by",
                DRIVEBY_PENDING: "In decodifica",
                DRIVEBY_FAILED: "Decodifica fallita",
                MANUAL: "Letto manuale",
                NOT_COLLECTED: "Non raccolta",
                NOT_READABLE: "Non leggibile",
              },
              decodeStatus: "Stato decodifica",
              decodeStatusValues: {
                PENDING: "In attesa",
                DECODED: "Decodata",
                NO_KEY: "Chiave mancante",
                METER_UNKNOWN: "Contatore sconosciuto",
                DECRYPT_FAILED: "Decifratura fallita",
                PARSE_FAILED: "Parsing fallito",
                UNSUPPORTED_MODE: "Modo non supportato",
                NEEDS_FORMAT: "Formato richiesto",
              },
              decodeBuckets: {
                failed: "Fallita",
              },
              outcome: "Esito",
              outcomeValues: {
                // «Letto» come readingStateValues e statusValues: questo filtro vive
                // sulla pagina Letture, nella stessa griglia del tag di riga, e due
                // etichette allo stesso livello non possono nominare diversamente lo
                // stesso esito. L'intestazione di famiglia in legenda resta invece
                // «Raccolta», che è il nome dell'insieme, non del singolo esito.
                READ: "Letto",
                NOT_COLLECTED: "Non raccolta",
                NOT_READABLE: "Non leggibile",
              },
              acquisitionMethod: "Modalità",
              acquisitionValues: {
                DRIVEBY: "Drive-by",
                MANUAL: "Manuale",
              },
              acquisitionDisabledHint: "La modalità si applica solo alle letture (esito 'Letto')",
              electionTitle: "Elezione",
              electionMode: "Modalità elezione",
              electionModeValues: {
                AUTO: "Automatica",
                MANUAL: "Manuale",
              },
              electedManual: "Eletta manualmente",
              electedBy: "Eletta da",
              electedAt: "Eletta il",
              electionNote: "Motivazione",
              restoreAuto: "Ripristina automatica",
              restoreAutoConfirm: {
                title: "Ripristinare l'elezione automatica?",
                description: "La lettura tornerà a essere determinata dall'ultimo invio ricevuto.",
                confirm: "Ripristina",
                cancel: "Annulla",
              },
              restoreAutoSuccess: "Elezione automatica ripristinata.",
              electError: "Impossibile aggiornare l'elezione. Riprova.",
              reasonCode: "Motivo",
              reasonValues: {
                GATE_CLOSED: "Cancello chiuso",
                BAD_WEATHER: "Maltempo",
                ACCESS_DENIED: "Accesso negato",
                NO_TIME_AT_VISIT: "Tempo insufficiente",
                METER_REMOVED: "Contatore rimosso",
                METER_BROKEN: "Contatore guasto",
                METER_REPLACED: "Contatore sostituito",
                BUILDING_DEMOLISHED: "Edificio demolito",
                PERMANENTLY_INACCESSIBLE: "Permanentemente inaccessibile",
                OTHER: "Altro",
              },
              value: "Valore",
              hasImage: "Foto",
              operatorId: "Operatore",
              operatorName: "Operatore",
              readingAt: "Data lettura",
              recordedAt: "Data invio",
              receivedAt: "Data invio",
              serverReceivedAt: "Ricevuta dal server",
              address: "Indirizzo",
              rssi: "RSSI",
              ocrText: "Testo OCR",
              keyVersion: "Versione chiave",
              decoded: "Decodificata",
              decodeError: "Errore decodifica",
              attempts: "Tentativi",
              lastAttemptAt: "Ultimo tentativo",
              frameTimestamp: "Timestamp frame",
              framePayload: "Payload frame",
              tplStatusByte: "Byte di stato TPL",
              errorFlags: "Flag di errore dispositivo",
              wmbusVersion: "Versione wM-Bus",
              tabs: {
                reading: "Lettura",
                history: "Storico invii",
                evidence: "Foto",
                technical: "Dettagli tecnici",
              },
              reDecode: "Re-decodifica",
              reDecodeSuccess: "Lettura ri-decodificata.",
              reDecodeError: "Impossibile ri-decodificare la lettura. Riprova.",
              notes: "Note",
              evidence: "Foto evidenza",
              noEvidence: "Nessuna foto per questa lettura.",
              imageLoading: "Caricamento dell'immagine in corso…",
              imageNoPrivilege: "Non disponi del privilegio per visualizzare le immagini.",
              anonymize: "Elimina foto",
              anonymized: "Foto eliminata. L'immagine non è più disponibile.",
              anonymizeSuccess: "Foto eliminata correttamente.",
              anonymizeError: "Impossibile eliminare la foto. Riprova.",
              anonymizeConfirm: {
                title: "Eliminare la foto?",
                description: "L'immagine verrà resa non più scaricabile. L'operazione è irreversibile.",
                confirm: "Elimina",
                cancel: "Annulla",
              },
              noResult: "Nessuna lettura materializzata per questa location.",
              empty: "Nessuna lettura trovata.",
            },
            submission: {
              tableTitle: "Storico invii",
              serverReceivedAt: "Ricevuta dal server",
              status: "Esito",
              value: "Valore",
              reasonCode: "Motivo",
              operatorName: "Operatore",
              hasImage: "Foto",
              elected: "Eletta",
              actions: "Azioni",
              pin: "Pin",
              pinSuccess: "Invio eletto come lettura.",
              pinConfirm: {
                title: "Eleggere questo invio?",
                description: "Questo invio diventerà la lettura ufficiale (override manuale).",
                note: "Motivazione (opzionale)",
                confirm: "Eleggi",
                cancel: "Annulla",
              },
              empty: "Nessun invio per questa location.",
            },
          },
          enums: {
            role: {
              TENANT_ADMIN: "Amministratore",
              DISPATCHER: "Responsabile giri",
              OPERATOR: "Operatore",
              READONLY: "Sola lettura",
              SAC_INGEST: "Sac",
            },
            privilege: {
              TENANT_MANAGE: "Gestione tenant (cross-tenant)",
              USER_VIEW: "Visualizzazione utenti",
              USER_MANAGE: "Gestione utenti",
              TEAM_VIEW: "Visualizzazione team",
              TEAM_MANAGE: "Gestione team",
              ROLE_VIEW: "Visualizzazione ruoli",
              ROLE_MANAGE: "Gestione ruoli",
              METER_VIEW: "Visualizzazione contatori",
              METER_MANAGE: "Gestione contatori",
              METER_KEY_VIEW: "Visualizzazione chiavi contatore",
              METER_KEY_MANAGE: "Gestione chiavi contatore",
              METER_MODEL_VIEW: "Visualizzazione modelli contatore",
              METER_MODEL_MANAGE: "Gestione modelli contatore",
              RUN_VIEW: "Visualizzazione campagne di raccolta",
              RUN_MANAGE: "Gestione campagne di raccolta",
              READING_SUBMIT: "Invio letture",
              TERRITORY_VIEW: "Visualizzazione territori",
              TERRITORY_MANAGE: "Gestione territori",
              FIELDOPS_VIEW: "Visualizzazione operazioni sul campo",
              FIELDOPS_MANAGE: "Gestione operazioni sul campo",
              FIELDOPS_OPERATE: "Esecuzione raccolte (operatore mobile)",
              IMAGING_VIEW: "Visualizzazione immagini",
              IMAGING_MANAGE: "Gestione immagini",
              REPORTING_VIEW: "Visualizzazione report",
            },
          },
          table: {
            export: {
              title: "Esporta",
              filtered: "Elementi Filtrati",
              all: "Tutti gli elementi",
            },
            delete: "Elimina",
            info: "Dettaglio",
            edit: "Modifica",
            add: "Aggiungi",
            import: "Importa",
            noData: "Nessun elemento disponibile",
            fetchError:
              "Attenzione! Si è verificato un errore durante il recupero degli elementi.",
            itemsPerPage: "Pagina",
            itemsTotal: "elementi totali",
            sort: {
              ascend: "Applica ordinamento crescente",
              descend: "Applica ordinamento decrescente",
              cancel: "Rimuovi ordinamento",
            },
            search: "Cerca",
            searchEmpty: "Non settato",
            reset: "Pulisci",
            refresh: "Aggiorna",
            close: "Chiudi",
            confirm: "Conferma",
            selectValue: "Seleziona un valore",
            selectValues: "Seleziona uno o più valori",
            enabled: "Abilitato",
            disabled: "Disabilitato",
            from: "Da...",
            to: "A...",
          },
          form: {
            selectElementToShow: "Selezionare l'elemento da visualizzare.",
            selectElementToEdit: "Selezionare l'elemento da modificare.",
            save: "Salva",
            cancel: "Annulla",
            loadError:
              "Attenzione! Si è verificato un errore durante il caricamento dei dati.",
            createSuccess: "L'elemento è stato creato correttamente.",
            createError:
              "Attenzione! Si è verificato un errore durante la creazione dell'elemento.",
            editSuccess: "L'elemento è stato modificato correttamente.",
            editError:
              "Attenzione! Si è verificato un errore durante la modifica dell'elemento.",
            removeTitle: "Eliminare gli elementi selezionati?",
            removeConfirm: "Si",
            removeCancel: "No",
            removeSuccess:
              "Gli elementi selezionato sono stati eliminati correttamente.",
            removeError:
              "Attenzione! Si è verificato un errore durante l'eliminazione degli elementi selezionati.",
            genericError:
              "Attenzione! Si è verificato un errore imprevisto.",
            reset: "Resetta",
            import: "Importa",
            importSuccess: "Gli elementi sono stati importati correttamente.",
            importEmpty:
              "Attenzione! Il file caricato non presenta elementi importabili.",
            importError:
              "Attenzione! Si è verificato un errore durante l'importazione degli elementi.",
            importTooLarge:
              "Attenzione! Il file caricato contiene più di 1500 elementi.",
            required: "Il campo {{attribute}} è obbligatorio.",
            input: {
              file: {
                select: "Seleziona file",
                error: "Attenzione! Il file {{name}} non è del tipo richiesto.",
              },
            },
            characters: {
              exactlyNumber:
                "Il campo {{attribute}} deve essere di {{len}} cifre.",
              exactlyString:
                "Il campo {{attribute}} deve essere di {{len}} caratteri.",
              minMaxNumber:
                "Il numero di cifre deve essere compreso tra {{min}} e {{max}}.",
              minMaxString:
                "Il numero di caratteri deve essere compreso tra {{min}} e {{max}}.",
              minNumber:
                "Il numero di cifre deve essere maggiore o uguale di {{min}}.",
              minString:
                "Il numero di caratteri deve essere maggiore o uguale di {{min}}.",
              maxNumber:
                "Il numero di cifre deve essere minore o uguale di {{max}}.",
              maxString:
                "Il numero di caratteri deve essere minore o uguale di {{max}}.",
            },
            numberRange: {
              minMax: "Il valore deve essere compreso tra {{min}} e {{max}}.",
              min: "Il valore deve essere maggiore o uguale di {{min}}.",
              max: "Il valore deve essere minore o uguale di {{max}}.",
            },
            validation: {
              default: "Il valore inserito non è valido.",
              email: "Indirizzo email non valido.",
              url: "L'URL inserito non è valido.",
              integer: "Il valore deve essere un numero intero.",
              float: "Il valore deve essere un numero decimale.",
              hex: "Il valore deve essere un numero esadecimale.",
              role: {
                code: "Il codice deve iniziare con una lettera maiuscola e contenere solo lettere maiuscole, cifre o underscore.",
                privilegeCodesMin: "Selezionare almeno un privilegio.",
              },
              user: {
                roleCodesMin: "Selezionare almeno un ruolo.",
                passwordMismatch: "Le password non coincidono.",
              },
              meter: {
                wmbusAddress: "L'indirizzo wM-Bus deve essere di 16 caratteri esadecimali (0-9, A-F).",
                serialNumberAscii: "Il numero di serie può contenere solo lettere e cifre non accentate, spazio e punteggiatura.",
              },
            },
            title: {
              info: "Dettaglio {{model}}",
              create: "Aggiungi {{model}}",
              edit: "Modifica {{model}}",
              import: "Importa {{model}}",
            },
          },
          importProgress: {
            title: {
              processing: "Importazione in corso",
              completed: "Importazione completata",
            },
            total: "Elementi totali",
            executed: "Inserimenti elaborati",
            successes: "Inserimenti eseguiti",
            failures: "Inserimenti falliti",
          },
          appheader: {
            dashboard: {
              title: "Dashboard",
            },
            admin: {
              title: "Amministrazione",
              users: "Utenti",
              roles: "Ruoli",
              teams: "Team",
            },
            catalog: {
              title: "Catalogo",
              meterModels: "Modelli contatore",
              meters: "Contatori",
              devices: "Dispositivi",
            },
            territory: {
              title: "Territorio",
              zones: "Zone",
              areas: "Aree",
            },
            fieldops: {
              title: "Operazioni",
              requests: "Campagne",
              runs: "Giro di raccolta",
              readings: "Letture",
            },
            reporting: {
              title: "Report",
              reports: "Archivio report",
            },
            devices: "Dispositivi",
            account: {
              profile: "Profilo",
              switchSubTenant: "Cambia ambiente",
              logout: "Disconnetti",
            },
          },
          console: {
            title: "Console di piattaforma",
            menu: { meterModels: "Modelli contatore", tenants: "Tenant", personas: "Persone", firmware: "Firmware", devices: "Dispositivi", geoCoverage: "Copertura mappe" },
            home: {
              title: "Console di piattaforma",
              subtitle: "Seleziona una voce dal menu per iniziare.",
            },
            account: {
              logout: "Logout",
            },
          },
          loginpage: {
            title: "DriveBy",
            subtitle: "Reindirizzamento al provider di autenticazione…",
            hint: "Problemi di accesso? Contatta il supporto.",
            useAnotherIdentifier: "Usa un altro identificativo",
            sessionError: "Sessione non valida. Riprova o usa un altro identificativo.",
            identifier: {
              subtitle: "Inserisci il tuo identificativo per continuare.",
              placeholder: "Email o nome utente",
              continue: "Continua",
              back: "Indietro",
              chooseCustomer: "Seleziona il cliente:",
              notFound: "Credenziali non valide.",
              error: "Impossibile contattare il servizio di autenticazione. Riprova.",
            },
            errors: {
              internalError: "Si è verificato un errore durante l'autenticazione.",
            }
          },
          subtenantpage: {
            title: "Cambia ambiente",
            subtitle: "Seleziona l'ambiente su cui operare. La scelta si applica a tutte le operazioni successive.",
            empty: "Nessun ambiente disponibile.",
            // Ruoli e privilegi sono per ambiente: il cambio non è istantaneo,
            // si aspetta che il profilo del nuovo ambiente sia stato caricato.
            switching: "Carico i permessi dell'ambiente…",
            // I due esiti terminali di /me. Nominano la causa e l'azione: da qui
            // l'unica uscita è scegliere un altro ambiente.
            problem: {
              USER_NOT_FOUND: "Nessun profilo in questo ambiente: il tuo utente non vi è registrato. Scegli un altro ambiente qui sotto.",
              SUBTENANT_REQUIRED: "Non è stato possibile determinare l'ambiente attivo. Scegline uno qui sotto per continuare.",
            },
          },
          dashboardpage: {
            title: "Dashboard",
            adminTitle: "Dashboard letture",
            realtimeBadge: "in tempo reale",
            welcome: "Benvenuto nella piattaforma DriveBy.",
            refresh: "Aggiorna",
            loadError: "Impossibile caricare i dati della dashboard.",
            periodRange: "{{start}} – {{end}}",
            periodPanel: {
              title: "Analisi del periodo",
            },
            period: {
              today: "Oggi",
              week: "Settimana",
              month: "Mese",
              weekHint: "Dal lunedì corrente a oggi",
              monthHint: "Dal 1° del mese a oggi",
            },
            kpi: {
              activeRuns: "Giri attivi",
              activeRunsHint: "Assegnati e in corso",
              activeRunsCta: "Vedi giri attivi",
              toBeTerminated: "Giri da completare",
              toBeTerminatedHint: "Richiedono intervento",
              toBeTerminatedCta: "Vedi giri da completare",
              coverage: "Copertura",
              requestAlerts: "Campagne in errore",
              requestAlertsHint: "Scadute o non suddivise",
              requestAlertsCta: "Vedi campagne in errore",
              runsScopeTooltip: "Il conteggio si riferisce al periodo selezionato; la lista mostra tutti i giri in questo stato.",
            },
            coverage: {
              drivebyDecoded: "Drive-by decodati",
              drivebyPending: "Drive-by in attesa/falliti",
            },
            recentRuns: {
              title: "Ultimi giri",
              scopeHint: "Ultimi 10 giri in assoluto, indipendenti dal periodo selezionato",
              runId: "Giro",
              finalStatus: "Stato",
              driveby: "Drive-by",
              drivebyTooltip: "Acquisiti {{acquired}}, decodati {{decoded}}",
              coveragePct: "Copertura",
              totalLocations: "Location",
              completedAt: "Concluso il",
              empty: "Nessun giro recente.",
            },
            trends: {
              title: "Trend letture",
              readingsTotal: "Letture totali",
              readingsSuccessful: "Letture riuscite",
              readingsUnsuccessful: "Letture non riuscite",
              runsCompleted: "Giri completati",
              successRate: "Success rate",
              empty: "Nessun dato nel periodo selezionato",
            },
            failurereasons: {
              title: "Motivi di fallimento",
              unspecified: "(non specificato)",
              count: "Occorrenze",
              empty: "Nessun fallimento nel periodo selezionato",
            },
          },
          reporting: {
            types: {
              DAILY: "Giornaliero",
              WEEKLY: "Settimanale",
              MONTHLY: "Mensile",
            },
            contentKinds: {
              RUNS: "Giri",
              READINGS: "Letture",
            },
            statuses: {
              RUNNING: "In generazione",
              READY: "Pronto",
              FAILED: "Fallito",
            },
            archive: {
              tableTitle: "Archivio report",
              type: "Tipo",
              contentKind: "Contenuto",
              period: "Periodo",
              status: "Stato",
              rowCount: "Righe",
              sizeBytes: "Dimensione",
              generatedBy: "Generato da",
              completedAt: "Completato il",
              actions: "Azioni",
              download: "Scarica",
              downloadError: "Impossibile scaricare il report. Riprova.",
              empty: "Nessun report disponibile.",
              dateFrom: "Da",
              dateTo: "A",
            },
          },
          profilepage: {
            title: "Profilo utente",
            username: "Username",
            roles: "Ruoli",
            privileges: "Privilegi",
            firstName: "Nome",
            lastName: "Cognome",
            email: "Email",
            identityTitle: "I tuoi dati",
            // Lo username non è in UpdateOwnProfileRequest: è immutabile per contratto.
            usernameHint: "Lo username non è modificabile.",
            save: "Salva",
            saveSuccess: "Dati aggiornati.",
            saveError: "Non è stato possibile aggiornare i dati.",
            loadError: "Non è stato possibile caricare i tuoi dati.",
          },
          notfoundpage: {
            title: "Risorsa non trovata",
            subTitle: "Attenzione, la risorsa richiesta è inesistente.",
          },
          forbiddenpage: {
            title: "Accesso negato",
            subTitle:
              "Attenzione, non si dispone dei privilegi necessari per accedere alla pagina.",
          },
          system: {
            loginNeeded: "Attenzione! La sessione è scaduta. Effettuare il login.",
            maintenance: {
              notice: "Manutenzione della piattaforma in corso: riprova tra {{minutes}} minuti.",
              title: "Manutenzione della piattaforma in corso",
              retrying: "L'accesso riprende da solo: nuovo tentativo tra {{minutes}} minuti.",
            },
          },
          errors: {
            title: "Dettagli errore",
            user: {
              selfLockout: "Non puoi rimuovere a te stesso il ruolo {{role}}: l'operazione causerebbe il blocco del tuo accesso.",
            },
            backend: {
              INTERNAL_ERROR: "Errore imprevisto del server. Riprova tra qualche istante o contatta il supporto.",
              USERNAME_ALREADY_EXISTS: "Username \"{{username}}\" già in uso.",
              TEAM_NAME_ALREADY_EXISTS: "Esiste già un team con nome \"{{name}}\".",
              ROLE_ALREADY_EXISTS: "Esiste già un ruolo con codice \"{{roleCode}}\".",
              ROLE_IN_USE: "Il ruolo \"{{roleCode}}\" è assegnato a {{userCount}} utenti attivi; riassegnali prima di eliminarlo.",
              TEAM_HAS_OPEN_RUNS: "Il team ha {{openRunCount}} campagne ancora aperte; chiudile prima di eliminarlo.",
              // Gemello del precedente, e finora non tradotto: il 409 usciva in
              // inglese in due punti (toast e pannello CrudFormErrors) su
              // un'azione admin ordinaria. `assignmentCount` arriva dai details,
              // che mapBackendError spande già nel contesto di interpolazione:
              // nessuna modifica di codice.
              TEAM_HAS_TERRITORY_ASSIGNMENTS: "Il team è assegnato a {{assignmentCount}} tra aree e zone attive; riassegnale prima di eliminarlo.",
              SELF_LOCKOUT_FORBIDDEN: "Non puoi rimuovere a te stesso il ruolo {{roleCode}}: l'operazione causerebbe il blocco del tuo accesso.",
              KEYCLOAK_SYNC_FAILED: "Sincronizzazione con Keycloak fallita. Riprova tra qualche istante.",
              GEO_DESIRED_REVISION_STALE: "Un altro amministratore ha applicato la revisione {{latestRevision}} nel frattempo.",
              GEO_AREA_UNKNOWN: "Codici di area sconosciuti: {{codes}}.",
              GEO_AREAS_OVERLAP: "Una regione e una sua provincia scelte insieme.",
              USER_NOT_FOUND: "Utente non trovato.",
              ROLE_NOT_FOUND: "Ruolo non trovato.",
              TEAM_NOT_FOUND: "Team non trovato.",
              METER_KEY_ALREADY_EXISTS: "Esiste già una chiave corrente per questo contatore. Usa Sostituisci o Revoca.",
              // 409 della rotate a materiale identico (piano B): esito
              // informativo, non errore. Attenzione alla formulazione §12:
              // ricaricare le STESSE chiavi non fa più niente, ma una chiave
              // nuova o corretta fa ancora ripartire la decodifica.
              KEY_UNCHANGED: "La chiave inserita è già quella corrente: nessuna modifica, e nessun nuovo tentativo di decodifica avviato. Per ritentare le letture non decodificate usa «Re-decodifica» dal giro o dalla lettura; una chiave davvero nuova riavvia la decodifica da sé.",
              METER_KEY_NOT_FOUND: "Nessuna chiave corrente per il contatore.",
              // Il rimedio va ripetuto qui: mapBackendError preferisce la chiave
              // locale a body.message, quindi il messaggio del backend — che dal
              // 2026-08-08 lo spiega — non raggiunge mai l'operatore.
              METER_HAS_NO_WMBUS_ADDRESS: "Il contatore non ha un indirizzo wM-Bus: non è possibile registrare o sostituire una chiave. Aggiungi prima l'indirizzo con una riga di import contatori, poi registra la chiave.",
              INVALID_KEY_FORMAT: "Formato chiave non valido ({{reasonCode}}). Inserisci 32 o 64 caratteri esadecimali.",
              METER_ALREADY_EXISTS: "Esiste già un contatore con indirizzo wM-Bus \"{{wmbusAddress}}\".",
              METER_SERIAL_ALREADY_EXISTS: "Esiste già un contatore attivo con questo numero di serie per il modello selezionato. Il numero di serie deve essere unico a parità di modello.",
              VALIDATION_FAILED: "Alcuni campi non sono validi: controlla i valori inseriti.",
              DB_UNIQUE_VIOLATION: "Il valore inserito è già in uso: non può essere duplicato.",
              DB_CHECK_VIOLATION: "Un valore inserito non rispetta un vincolo del database ({{constraint}}).",
              INVALID_REQUEST_BODY: "Richiesta non valida: riprova o contatta il supporto se il problema persiste.",
              METER_NOT_IN_ACTIVE_ZONE: "Uno o più contatori non appartengono a una zona attiva.",
              // §15: seriale senza modello che risolve a più contatori ACTIVE.
              // L'azione è qualificare (vendor;modelCode), non «caricare».
              METER_AMBIGUOUS_SERIAL: "Uno o più numeri di serie corrispondono a più contatori attivi: qualificali con vendor e modelCode.",
              METER_ALREADY_DEACTIVATED: "Il contatore è già disattivato.",
              METER_MODEL_ALREADY_EXISTS: "Esiste già un modello contatore con produttore \"{{vendor}}\" e nome \"{{modelCode}}\".",
              METER_NOT_FOUND: "Contatore non trovato.",
              METER_MODEL_NOT_FOUND: "Il modello selezionato non è più disponibile.",
              IMPORT_JOB_NOT_FOUND: "Job di importazione non trovato.",
              ZONE_NOT_FOUND: "Zona non trovata.",
              ZONE_RETIRED: "La zona è stata ritirata: non sono consentite modifiche al suo interno (aree o contatori).",
              // Bulk zona: nomina il numero, non gli id — l'elenco dei colpevoli
              // (details.missingMeterIds) lo mostra il chiamante, che sa quanto
              // spazio ha e sa offrire il riprova senza di loro.
              METERS_NOT_FOUND: "Uno o più contatori selezionati non sono più disponibili (disattivati o rimossi): l'operazione è stata annullata per intero.",
              BULK_TOO_LARGE: "L'operazione toccherebbe {{requested}} contatori, oltre il limite di {{max}} per singola richiesta.",
              AREA_NOT_FOUND: "Area non trovata.",
              AREA_POLYGON_INTERSECTS_EXISTING: "Il poligono interseca aree esistenti della stessa zona.",
              REQUEST_NOT_FOUND: "Campagna di raccolta non trovata.",
              RUN_NOT_FOUND: "Giro di raccolta non trovato.",
              REQUEST_DUPLICATE: "Esiste già una campagna con questo nome.",
              // Ripiego del 422 RUN_TOO_LARGE senza elenco leggibile: niente {{limit}},
              // che con details incompleti uscirebbe letterale.
              RUN_TOO_LARGE: "La campagna non è stata creata: almeno un giro supererebbe il tetto di location per giro.",
              REQUEST_INVALID_TRANSITION: "La campagna è già in uno stato terminale: non è possibile annullarla.",
              RUN_INVALID_TRANSITION: "Il giro non è finalizzabile: è già terminato oppure non ancora attivato.",
              LOCATION_RUN_MISMATCH: "La location indicata non appartiene a questo giro.",
              LOCATION_NOT_FOUND: "Location non trovata.",
              SUBMISSION_NOT_FOUND: "Invio non trovato.",
              SUBMISSION_LOCATION_MISMATCH: "L'invio selezionato non appartiene a questa location.",
              PAGE_SIZE_TOO_LARGE: "La pagina richiesta supera la dimensione massima consentita.",
              INVALID_FILTER_VALUE: "Valore \"{{value}}\" non valido per il filtro \"{{field}}\".",
              IMAGE_NOT_FOUND: "Immagine evidenza non trovata o già eliminata.",
              TENANT_NOT_FOUND: "Tenant non trovato.",
              TENANT_NOT_SUSPENDED: "Il tenant non è sospeso (stato attuale: {{currentStatus}}): impossibile riattivarlo.",
              PERSONA_NOT_FOUND: "Persona non trovata.",
              UNSUPPORTED_FILTER_OPERATOR: "Operatore di filtro \"{{operator}}\" non supportato sul campo \"{{field}}\".",
            },
          },
        },
      },
    },
  });

export default i18n;

export const pickerLocale: PickerLocale = {
  lang: {
    ...DatePickerLocale.lang,
    placeholder: "Selezionare la data",
    rangePlaceholder: ["Data d'inizio", "Data di fine"],
    shortWeekDays: ["Do", "Lu", "Ma", "Me", "Gi", "Ve", "Sa"],
    shortMonths: [
      "Gen",
      "Feb",
      "Mar",
      "Apr",
      "Mag",
      "Giu",
      "Lug",
      "Ago",
      "Set",
      "Ott",
      "Nov",
      "Dic",
    ],
  },
  timePickerLocale: {
    ...TimePickerLocale,
  },
};
